import React, { useState, useEffect } from 'react';
import { apiUrl } from '../../utils/apiBase';
import { XMarkIcon, PlusIcon, TrashIcon, PencilSquareIcon, EyeIcon, EyeSlashIcon, ArrowRightIcon, EnvelopeIcon, CogIcon, ChatBubbleLeftRightIcon, UserIcon, LinkIcon, ArrowsRightLeftIcon, ClockIcon as ClockIconOutline, Squares2X2Icon, ChevronRightIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import type { Flow as FlowType, Automacao as AutomacaoType, EmailTemplate } from '../../types';

// Interface local para contornar problemas de tipo
interface Flow extends Omit<FlowType, 'name'> {
  id: string;
  nome: string;
  etapas: FlowType['etapas'];
}

interface NovaAutomacaoProps {
  flows: Flow[];
  onSave: (automacao: any) => void;
  onClose: () => void;
  automacaoExistente?: any;
  session?: any;
}

const NovaAutomacao: React.FC<NovaAutomacaoProps> = ({ 
  flows, 
  onSave, 
  onClose, 
  automacaoExistente, 
  session 
}) => {
  const [selectedFlow, setSelectedFlow] = useState<Flow | null>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [colaboradores, setColaboradores] = useState<any[]>([]);
  const [configsCardConectado, setConfigsCardConectado] = useState<any[]>([]);
  const [documentosTemplates, setDocumentosTemplates] = useState<any[]>([]);

  const [automacao, setAutomacao] = useState<Omit<AutomacaoType, 'id'>>({
    nome: '',
    flowId: '',
    etapaOrigemId: '',
    etapaDestinoId: '',
    condicoes: [],
    acoes: [],
    tipo: 'mover_card',
    createdAt: new Date(),
    updatedAt: new Date(),
    status: 'ativo',
    empresaId: session?.usuario?.empresaId || ''
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [regrasMinimizadas, setRegrasMinimizadas] = useState<Set<string>>(new Set());
  const [acoesMinimizadas, setAcoesMinimizadas] = useState<Set<number>>(new Set());
  const [bulkPanelAberto, setBulkPanelAberto] = useState(false);
  const [bulkCampoId, setBulkCampoId] = useState('');
  const [bulkValor, setBulkValor] = useState('');

  const toggleAcaoMinimizada = (acaoIndex: number) => {
    setAcoesMinimizadas(prev => {
      const novo = new Set(prev);
      if (novo.has(acaoIndex)) novo.delete(acaoIndex);
      else novo.add(acaoIndex);
      return novo;
    });
  };

  const toggleRegraMinimizada = (acaoIndex: number, regraIndex: number) => {
    const key = `${acaoIndex}-${regraIndex}`;
    setRegrasMinimizadas(prev => {
      const novo = new Set(prev);
      if (novo.has(key)) {
        novo.delete(key);
      } else {
        novo.add(key);
      }
      return novo;
    });
  };

  const minimizarTodasRegras = (acaoIndex: number, totalRegras: number) => {
    setRegrasMinimizadas(prev => {
      const novo = new Set(prev);
      const todasMinimizadas = Array.from({ length: totalRegras }, (_, i) => `${acaoIndex}-${i}`).every(k => novo.has(k));
      for (let i = 0; i < totalRegras; i++) {
        const key = `${acaoIndex}-${i}`;
        if (todasMinimizadas) {
          novo.delete(key);
        } else {
          novo.add(key);
        }
      }
      return novo;
    });
  };

  // Inicializar com dados da automação existente se estiver editando
  useEffect(() => {
    if (automacaoExistente) {
  console.log('Carregando automação existente:', automacaoExistente);
      
  // Ao abrir edição, minimizar todas as ações por padrão
  const qtdAcoes = (automacaoExistente.acoes || []).length;
  if (qtdAcoes > 0) {
    setAcoesMinimizadas(new Set(Array.from({ length: qtdAcoes }, (_, i) => i)));
  }

  // Garantir que o campo nome existe e que as ações são preservadas
      const automacaoComNome = {
        ...automacaoExistente,
        nome: automacaoExistente.nome || '', // Se não tem nome, usar string vazia
        acoes: automacaoExistente.acoes || [], // Preservar ações existentes
        condicoes: automacaoExistente.condicoes || [] // Preservar condições existentes
      };
      
  console.log('Automação mapeada para edição:', automacaoComNome);
  console.log('Ações preservadas:', automacaoComNome.acoes);
      
  // Normalização de compatibilidade: converter campoId por nome/chave -> id do campo
      const normalizarAutomacao = (auto: any, flowAtual?: any) => {
        try {
          const etapaId = auto.etapaOrigemId;
          const etapa = flowAtual?.etapas?.find((e: any) => e.id === etapaId);
          const campos: any[] = etapa?.campos || [];
          const ids = new Set<string>(campos.map((c: any) => String(c.id)));

          const resolverCampoId = (valor: any): string => {
            const val = String(valor ?? '');
            if (!val) return '';
            if (ids.has(val)) return val; // já é id
            // procurar por nome igual (case-insensitive) ou chave igual
            const alvo = campos.find((c: any) =>
              String(c.nome).toLowerCase() === val.toLowerCase() ||
              String(c.chave || '').toLowerCase() === val.toLowerCase() ||
              String(c.key || '').toLowerCase() === val.toLowerCase()
            );
            return alvo ? String(alvo.id) : val; // se não achar, mantém como estava
          };

          const normalizarRegrasVisibilidade = (regras: any[]) =>
            (regras || []).map((r: any) => {
              const condicao = { ...(r?.condicao || {}) };
              if (condicao.campoId) condicao.campoId = resolverCampoId(condicao.campoId);
              // Normalizar campoIds (multi-select SE)
              if (Array.isArray(condicao.campoIds) && condicao.campoIds.length > 0) {
                condicao.campoIds = condicao.campoIds.map((id: string) => resolverCampoId(id));
              } else if (condicao.campoId) {
                condicao.campoIds = [condicao.campoId];
              }
              const regraNova: any = { ...r, condicao };
              
              // Compatibilidade: se temEntao não existe, assume true (regras antigas tinham ENTÃO sempre ativo)
              if (regraNova.temEntao === undefined) {
                regraNova.temEntao = true;
              }
              
              // elementos ENTÃO - PRESERVAR opcoesEspecificas
              if (Array.isArray(r?.elementosAfetados) && r.elementosAfetados.length > 0) {
                regraNova.elementosAfetados = r.elementosAfetados.map((el: any) => {
                  const idResolvido = resolverCampoId(el?.id || el?.nome);
                  const campoSel = campos.find(c => String(c.id) === String(idResolvido));
                  return { 
                    id: idResolvido, 
                    tipo: 'campo', 
                    nome: campoSel?.nome || el?.nome || '',
                    opcoesEspecificas: el?.opcoesEspecificas
                  };
                });
              }
              // elementos SENÃO - PRESERVAR opcoesEspecificas
              if (Array.isArray(r?.elementosAfetadosSenao) && r.elementosAfetadosSenao.length > 0) {
                regraNova.elementosAfetadosSenao = r.elementosAfetadosSenao.map((el: any) => {
                  const idResolvido = resolverCampoId(el?.id || el?.nome);
                  const campoSel = campos.find(c => String(c.id) === String(idResolvido));
                  return { 
                    id: idResolvido, 
                    tipo: 'campo', 
                    nome: campoSel?.nome || el?.nome || '',
                    opcoesEspecificas: el?.opcoesEspecificas
                  };
                });
              }
              return regraNova;
            });

          const normalizarRegrasComCondicao = (regras: any[]) =>
            (regras || []).map((r: any) => {
              const cond = { ...(r?.condicao || {}) };
              if (cond.campoId) cond.campoId = resolverCampoId(cond.campoId);
              return { ...r, condicao: cond };
            });

          const acoesNormalizadas = (auto.acoes || []).map((a: any) => {
            if (a?.tipo === 'exibir_ocultar') {
              return {
                ...a,
                configuracao: {
                  ...(a.configuracao || {}),
                  regras: normalizarRegrasVisibilidade(a.configuracao?.regras || [])
                }
              };
            }
            if (a?.tipo === 'enviar_email') {
              return {
                ...a,
                configuracao: {
                  ...(a.configuracao || {}),
                  regrasEmail: normalizarRegrasComCondicao(a.configuracao?.regrasEmail || [])
                }
              };
            }
            if (a?.tipo === 'mover_card') {
              return {
                ...a,
                configuracao: {
                  ...(a.configuracao || {}),
                  regrasMover: normalizarRegrasComCondicao(a.configuracao?.regrasMover || [])
                }
              };
            }
            return a;
          });

          return { ...auto, acoes: acoesNormalizadas };
        } catch (e) {
          console.warn('Falha ao normalizar automação carregada:', e);
          return auto;
        }
      };

      const flow = flows.find(f => f.id === automacaoExistente.flowId);
      const autoNormalizada = normalizarAutomacao(automacaoComNome, flow);

      setAutomacao(autoNormalizada);
      if (flow) {
        setSelectedFlow(flow);
      }

      // Minimizar todas as regras por padrão ao carregar
      const todasKeys = new Set<string>();
      (autoNormalizada.acoes || []).forEach((acao: any, ai: number) => {
        if (acao.tipo === 'exibir_ocultar') {
          (acao.configuracao?.regras || []).forEach((_: any, ri: number) => {
            todasKeys.add(`${ai}-${ri}`);
          });
        }
      });
      setRegrasMinimizadas(todasKeys);
    }
  }, [automacaoExistente, flows]);

  // Carregar templates de e-mail, WhatsApp e configurações de cards conectados
  useEffect(() => {
    carregarTemplates();
    carregarTemplatesWhatsApp();
    carregarConfigsCardConectado();
    carregarDocumentosTemplates();
  }, []);

  const carregarTemplates = async () => {
    setLoadingTemplates(true);
    try {
  const resp = await fetch(apiUrl('/templates-email'));
      const data = await resp.json();
      if (data?.success && Array.isArray(data.templates)) {
        // Mapear IDs genÃ©ricos (string ou number) mantendo o shape do EmailTemplate tipado
        const list = (data.templates as any[]).map((t: any) => ({
          id: typeof t.id === 'string' || typeof t.id === 'number' ? t.id : t._id || t.id,
          nome: t.nome,
          assunto: t.assunto,
          conteudo: t.conteudo || '',
          destinatarios: t.destinatarios || [],
          cc: t.cc || [],
          bcc: t.bcc || [],
          campos_utilizados: t.campos_utilizados || [],
          ativo: typeof t.ativo === 'boolean' ? t.ativo : Boolean(t.ativo),
          created_at: t.created_at || new Date().toISOString(),
          updated_at: t.updated_at || new Date().toISOString()
        }));
        setTemplates(list as any);
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const carregarTemplatesWhatsApp = async () => {
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const email = session?.usuario?.email || session?.usuario?.id;
      if (email) headers['X-User-Email'] = email;
      if (session?.usuario?.role) headers['X-User-Role'] = session.usuario.role;

      const resp = await fetch('/api/templates-whatsapp', { headers });
      const data = await resp.json();
      if (data?.success && Array.isArray(data.templates)) {
        setWhatsappTemplates(data.templates.map((t: any) => ({
          id: t._id || t.id,
          nome: t.nome,
          conteudo: t.conteudo || '',
          destinatarios: t.destinatarios || [],
          ativo: t.ativo !== false
        })));
      }
    } catch (error) {
      console.error('Erro ao carregar templates de WhatsApp:', error);
    }
  };

  const carregarConfigsCardConectado = async () => {
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const email = session?.usuario?.email || session?.usuario?.id;
      if (email) headers['X-User-Email'] = email;
      if (session?.usuario?.role) headers['X-User-Role'] = session.usuario.role;

      const empresaId = session?.usuario?.empresaId;
      const resp = await fetch(apiUrl(`/cards-conectados/configuracoes?empresaId=${empresaId || ''}`), { headers });
      const data = await resp.json();
      if (data?.success && Array.isArray(data.configuracoes)) {
        setConfigsCardConectado(data.configuracoes.filter((c: any) => c.ativo !== false));
      } else if (Array.isArray(data)) {
        setConfigsCardConectado(data.filter((c: any) => c.ativo !== false));
      }
    } catch (error) {
      console.error('Erro ao carregar configs de cards conectados:', error);
      setConfigsCardConectado([]);
    }
  };

  const carregarDocumentosTemplates = async () => {
    try {
      const empresaId = session?.usuario?.empresaId;
      if (!empresaId) return;
      const resp = await fetch(apiUrl(`/documentos/templates?empresaId=${empresaId}`));
      if (resp.ok) {
        const data = await resp.json();
        if (data?.success && Array.isArray(data.templates)) {
          setDocumentosTemplates(data.templates);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar documentos templates:', error);
    }
  };

  // Carregar colaboradores da empresa
  useEffect(() => {
    const carregarColaboradores = async () => {
      if (!session?.usuario?.empresaId) return;
      
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const email = session?.usuario?.email || session?.usuario?.id;
        if (email) headers['X-User-Email'] = email;
        
        const resp = await fetch(apiUrl(`/colaboradores?empresaId=${session.usuario.empresaId}`), { headers });
        if (resp.ok) {
          const data = await resp.json();
          console.log('📊 Dados de colaboradores recebidos:', data);
          
          // Verificar se é um array ou objeto com array dentro
          let colaboradoresArray = [];
          if (Array.isArray(data)) {
            colaboradoresArray = data;
          } else if (data?.colaboradores && Array.isArray(data.colaboradores)) {
            colaboradoresArray = data.colaboradores;
          } else if (data?.data && Array.isArray(data.data)) {
            colaboradoresArray = data.data;
          }
          
          console.log('📊 Colaboradores array final:', colaboradoresArray);
          setColaboradores(colaboradoresArray);
        }
      } catch (error) {
        console.error('Erro ao carregar colaboradores:', error);
        setColaboradores([]);
      }
    };
    
    carregarColaboradores();
  }, [session?.usuario?.empresaId]);

  const handleSave = () => {
    const errors = validarAutomacao();
    
    if (errors.length > 0) {
  alert('Por favor, corrija os seguintes erros:\n\n' + errors.join('\n'));
      return;
    }

  console.log('handleSave - Salvando automação com dados:', {
      nome: automacao.nome,
      flowId: automacao.flowId,
      etapaOrigemId: automacao.etapaOrigemId,
      acoes: automacao.acoes.length,
      dadosCompletos: automacao
    });

  console.log('Ações detalhadas:', automacao.acoes);
    
    // Se tem regras, mostrar detalhadamente
    automacao.acoes.forEach((acao, index) => {
      if (acao.configuracao && acao.configuracao.regras) {
  console.log(`Ação ${index + 1} - Regras:`, acao.configuracao.regras);
      }
    });

    onSave({
      ...automacao,
      updatedAt: new Date()
    });
  };

  const adicionarAcao = (tipo: 'mover_card' | 'enviar_email' | 'enviar_whatsapp' | 'exibir_ocultar' | 'atribuir_responsavel' | 'criar_card_conectado' | 'mover_card_conectado' | 'atribuir_api' | 'criar_card_template' | 'atualizar_campo') => {
    const novaAcao = { 
      tipo, 
      configuracao: tipo === 'mover_card' ? { etapaDestinoId: '' } :
                    tipo === 'enviar_email' ? { templateId: '', destinatarios: [], regrasEmail: [] } :
                    tipo === 'enviar_whatsapp' ? { templateId: '', destinatarios: [], regrasWhatsapp: [], enviarSomentePrimeiraVez: false } :
                    tipo === 'atribuir_responsavel' ? { regrasResponsavel: [] } :
                    tipo === 'criar_card_conectado' ? { configuracaoCardConectadoId: '', regrasCardConectado: [] } :
                    tipo === 'mover_card_conectado' ? { moverCardConectado: { fluxoDestinoConectadoId: '', etapaDestinoConectadoId: '', fluxoDestinoAtualId: '', etapaDestinoAtualId: '', copiarDadosConexao: true, direcao: 'origem' } } :
                    tipo === 'atribuir_api' ? { regrasApi: [] } :
                    tipo === 'criar_card_template' ? { criarCardTemplate: { fluxoDestinoId: '', etapaDestinoId: '', templateId: '', responsavelId: '', tituloCustom: '' } } :
                    tipo === 'atualizar_campo' ? { campos: [] } :
                    { regras: [] }
    };
    
    setAutomacao(prev => ({
      ...prev,
      acoes: [...prev.acoes, novaAcao]
    }));
  };

  const validarAutomacao = () => {
    const errors = [];
    const tipoGatilho = (automacao as any).tipoGatilho || 'etapa';
    const agendamento = (automacao as any).agendamento;
    
  if (!automacao.nome.trim()) errors.push('Digite um nome para a automação');
    if (!automacao.flowId) errors.push('Selecione um fluxo');
    if (tipoGatilho === 'etapa' && !automacao.etapaOrigemId) errors.push('Selecione uma etapa de origem');
    if (tipoGatilho === 'agendado') {
      if (!agendamento || !agendamento.frequencia) errors.push('Configure o agendamento (frequência e horário)');
      else {
        if (agendamento.frequencia === 'mensal' && (!agendamento.diaDoMes || agendamento.diaDoMes < 1 || agendamento.diaDoMes > 31)) {
          errors.push('Informe o dia do mês (1 a 31)');
        }
        if (agendamento.frequencia === 'semanal' && (typeof agendamento.diaDaSemana !== 'number' || agendamento.diaDaSemana < 0 || agendamento.diaDaSemana > 6)) {
          errors.push('Informe o dia da semana');
        }
        if (typeof agendamento.hora !== 'number' || agendamento.hora < 0 || agendamento.hora > 23) errors.push('Hora inválida');
        if (typeof agendamento.minuto !== 'number' || agendamento.minuto < 0 || agendamento.minuto > 59) errors.push('Minuto inválido');
      }
    }
  if (automacao.acoes.length === 0) errors.push('Adicione pelo menos uma ação');
    
    // Validar ações específicas
    automacao.acoes.forEach((acao, index) => {
      if (acao.tipo === 'mover_card' && !acao.configuracao.etapaDestinoId) {
  errors.push(`Ação ${index + 1}: Selecione a etapa de destino`);
      }
      if (acao.tipo === 'enviar_email') {
        const temRegras = Array.isArray(acao.configuracao.regrasEmail) && acao.configuracao.regrasEmail.length > 0;
        const temTemplateDireto = !!acao.configuracao.templateId;
        const temTemplateEmAlgumaRegra = temRegras && acao.configuracao.regrasEmail!.some((r: any) => {
          const entaoOk = r?.acaoEntao?.tipo === 'enviar' && (r?.acaoEntao?.templateId || acao.configuracao.templateId);
          const senaoOk = r?.acaoSenao?.tipo === 'enviar' && (r?.acaoSenao?.templateId || acao.configuracao.templateId);
          return entaoOk || senaoOk;
        });
        if (!temTemplateDireto && !temTemplateEmAlgumaRegra) {
          errors.push(`Ação ${index + 1}: Defina um template (global) ou em pelo menos uma regra de envio`);
        }
      }
      if (acao.tipo === 'enviar_whatsapp') {
        const temRegras = Array.isArray(acao.configuracao.regrasWhatsapp) && acao.configuracao.regrasWhatsapp.length > 0;
        const temTemplateDireto = !!acao.configuracao.templateId;
        const temTemplateEmAlgumaRegra = temRegras && acao.configuracao.regrasWhatsapp!.some((r: any) => {
          const entaoOk = r?.acaoEntao?.tipo === 'enviar' && (r?.acaoEntao?.templateId || acao.configuracao.templateId);
          const senaoOk = r?.acaoSenao?.tipo === 'enviar' && (r?.acaoSenao?.templateId || acao.configuracao.templateId);
          return entaoOk || senaoOk;
        });
        if (!temTemplateDireto && !temTemplateEmAlgumaRegra) {
          errors.push(`Ação ${index + 1}: Defina um template de WhatsApp (global) ou em pelo menos uma regra de envio`);
        }
      }
      if (acao.tipo === 'exibir_ocultar' && (!acao.configuracao.regras || acao.configuracao.regras.length === 0)) {
  errors.push(`Ação ${index + 1}: Configure pelo menos uma regra de exibição/ocultação`);
      }
      if (acao.tipo === 'atribuir_responsavel' && (!acao.configuracao.regrasResponsavel || acao.configuracao.regrasResponsavel.length === 0)) {
        errors.push(`Ação ${index + 1}: Configure pelo menos uma regra de atribuição de responsável`);
      }
      if (acao.tipo === 'criar_card_conectado') {
        const temConfigDireta = !!acao.configuracao.configuracaoCardConectadoId;
        const temRegras = Array.isArray(acao.configuracao.regrasCardConectado) && acao.configuracao.regrasCardConectado.length > 0;
        const temConfigEmRegra = temRegras && acao.configuracao.regrasCardConectado!.some((r: any) => {
          return r?.acaoEntao?.tipo === 'criar' && r?.acaoEntao?.configuracaoCardConectadoId;
        });
        if (!temConfigDireta && !temConfigEmRegra) {
          errors.push(`Ação ${index + 1}: Selecione uma configuração de card conectado`);
        }
      }
      if (acao.tipo === 'criar_card_template') {
        const cfg = (acao.configuracao as any).criarCardTemplate || {};
        if (!cfg.fluxoDestinoId) errors.push(`Ação ${index + 1}: Selecione o fluxo de destino`);
        if (!cfg.etapaDestinoId) errors.push(`Ação ${index + 1}: Selecione a etapa de destino`);
        if (!cfg.templateId) errors.push(`Ação ${index + 1}: Selecione um modelo (card personalizado)`);
      }
      if (acao.tipo === 'atualizar_campo') {
        const linhas = (acao.configuracao as any).campos || [];
        if (linhas.length === 0) {
          errors.push(`Ação ${index + 1}: Adicione pelo menos um campo para atualizar`);
        } else if (linhas.some((l: any) => !l?.campoId)) {
          errors.push(`Ação ${index + 1}: Há campo sem seleção na lista de atualização`);
        }
      }
    });
    
    return errors;
  };

  const atualizarAcao = (acaoIndex: number, novaAcao: any) => {
    setAutomacao(prev => ({
      ...prev,
      acoes: prev.acoes.map((acao, i) => 
        i === acaoIndex ? novaAcao : acao
      )
    }));
  };

  const removerAcao = (acaoIndex: number) => {
    setAutomacao(prev => ({
      ...prev,
      acoes: prev.acoes.filter((_, i) => i !== acaoIndex)
    }));
  };

  const duplicarAcao = (acaoIndex: number) => {
    setAutomacao(prev => {
      const original = prev.acoes[acaoIndex];
      if (!original) return prev;
      // Deep clone para não compartilhar referências de configuração
      let copia: any;
      try {
        copia = JSON.parse(JSON.stringify(original));
      } catch {
        copia = { ...original, configuracao: { ...(original.configuracao || {}) } };
      }
      const novasAcoes = [...prev.acoes];
      novasAcoes.splice(acaoIndex + 1, 0, copia);
      return { ...prev, acoes: novasAcoes };
    });
  };

  const atualizarRegraVisibilidade = (acaoIndex: number, regraIndex: number, novaRegra: any) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const novasRegras = [...(acao.configuracao.regras || [])];
      novasRegras[regraIndex] = novaRegra;
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regras: novasRegras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const adicionarCondicaoE = (acaoIndex: number, regraIndex: number, tipoLogico: 'E' | 'OU' = 'E') => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const novasRegras = [...(acao.configuracao.regras || [])];
      const regra = { ...novasRegras[regraIndex] };
      const condicoesAdicionais = [...(regra.condicoesAdicionais || [])];
      condicoesAdicionais.push({ campoId: '', operador: 'preenchido' as const, valor: '', tipoLogico });
      regra.condicoesAdicionais = condicoesAdicionais;
      novasRegras[regraIndex] = regra;
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regras: novasRegras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const removerCondicaoE = (acaoIndex: number, regraIndex: number, condIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const novasRegras = [...(acao.configuracao.regras || [])];
      const regra = { ...novasRegras[regraIndex] };
      const condicoesAdicionais = [...(regra.condicoesAdicionais || [])];
      condicoesAdicionais.splice(condIndex, 1);
      regra.condicoesAdicionais = condicoesAdicionais.length > 0 ? condicoesAdicionais : undefined;
      novasRegras[regraIndex] = regra;
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regras: novasRegras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const atualizarCondicaoE = (acaoIndex: number, regraIndex: number, condIndex: number, novaCondicao: any) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const novasRegras = [...(acao.configuracao.regras || [])];
      const regra = { ...novasRegras[regraIndex] };
      const condicoesAdicionais = [...(regra.condicoesAdicionais || [])];
      condicoesAdicionais[condIndex] = novaCondicao;
      regra.condicoesAdicionais = condicoesAdicionais;
      novasRegras[regraIndex] = regra;
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regras: novasRegras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  // Funções para ações ENTÃO adicionais
  const adicionarEntaoAdicional = (acaoIndex: number, regraIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const novasRegras = [...(acao.configuracao.regras || [])];
      const regra = { ...novasRegras[regraIndex] };
      const extras = [...(regra.acoesEntaoAdicionais || [])];
      extras.push({ acao: 'esconder', elemento: { id: '', tipo: 'campo' as const, nome: '' } });
      regra.acoesEntaoAdicionais = extras;
      novasRegras[regraIndex] = regra;
      novasAcoes[acaoIndex] = { ...acao, configuracao: { ...acao.configuracao, regras: novasRegras } };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const removerEntaoAdicional = (acaoIndex: number, regraIndex: number, extraIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const novasRegras = [...(acao.configuracao.regras || [])];
      const regra = { ...novasRegras[regraIndex] };
      const extras = [...(regra.acoesEntaoAdicionais || [])];
      extras.splice(extraIndex, 1);
      regra.acoesEntaoAdicionais = extras.length > 0 ? extras : undefined;
      novasRegras[regraIndex] = regra;
      novasAcoes[acaoIndex] = { ...acao, configuracao: { ...acao.configuracao, regras: novasRegras } };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const atualizarEntaoAdicional = (acaoIndex: number, regraIndex: number, extraIndex: number, dados: any) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const novasRegras = [...(acao.configuracao.regras || [])];
      const regra = { ...novasRegras[regraIndex] };
      const extras = [...(regra.acoesEntaoAdicionais || [])];
      extras[extraIndex] = { ...extras[extraIndex], ...dados };
      regra.acoesEntaoAdicionais = extras;
      novasRegras[regraIndex] = regra;
      novasAcoes[acaoIndex] = { ...acao, configuracao: { ...acao.configuracao, regras: novasRegras } };
      return { ...prev, acoes: novasAcoes };
    });
  };

  // Funções para ações SENÃO adicionais
  const adicionarSenaoAdicional = (acaoIndex: number, regraIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const novasRegras = [...(acao.configuracao.regras || [])];
      const regra = { ...novasRegras[regraIndex] };
      const extras = [...(regra.acoesSenaoAdicionais || [])];
      extras.push({ acao: 'mostrar', elemento: { id: '', tipo: 'campo' as const, nome: '' } });
      regra.acoesSenaoAdicionais = extras;
      novasRegras[regraIndex] = regra;
      novasAcoes[acaoIndex] = { ...acao, configuracao: { ...acao.configuracao, regras: novasRegras } };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const removerSenaoAdicional = (acaoIndex: number, regraIndex: number, extraIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const novasRegras = [...(acao.configuracao.regras || [])];
      const regra = { ...novasRegras[regraIndex] };
      const extras = [...(regra.acoesSenaoAdicionais || [])];
      extras.splice(extraIndex, 1);
      regra.acoesSenaoAdicionais = extras.length > 0 ? extras : undefined;
      novasRegras[regraIndex] = regra;
      novasAcoes[acaoIndex] = { ...acao, configuracao: { ...acao.configuracao, regras: novasRegras } };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const atualizarSenaoAdicional = (acaoIndex: number, regraIndex: number, extraIndex: number, dados: any) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const novasRegras = [...(acao.configuracao.regras || [])];
      const regra = { ...novasRegras[regraIndex] };
      const extras = [...(regra.acoesSenaoAdicionais || [])];
      extras[extraIndex] = { ...extras[extraIndex], ...dados };
      regra.acoesSenaoAdicionais = extras;
      novasRegras[regraIndex] = regra;
      novasAcoes[acaoIndex] = { ...acao, configuracao: { ...acao.configuracao, regras: novasRegras } };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const adicionarRegraVisibilidade = (acaoIndex: number) => {
    const novaRegra = {
      condicao: { campoId: '', operador: 'preenchido' as const, valor: '' },
      acao: 'esconder' as const,
      elementosAfetados: [{ id: '', tipo: 'campo' as const, nome: '' }],
      temEntao: true // Por padrão, novas regras vêm com ENTÃO ativo
    };

    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const regrasExistentes = acao.configuracao.regras || [];
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regras: [...regrasExistentes, novaRegra] }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const removerRegraVisibilidade = (acaoIndex: number, regraIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const novasRegras = (acao.configuracao.regras || []).filter((_, i) => i !== regraIndex);
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regras: novasRegras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  // Regras de envio de e-mail (SE/ENTÃO/SENÃO)
  const atualizarRegraEmail = (acaoIndex: number, regraIndex: number, novaRegra: any) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const regras = [...(acao.configuracao.regrasEmail || [])];
      regras[regraIndex] = novaRegra;
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasEmail: regras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const adicionarRegraEmail = (acaoIndex: number) => {
    const novaRegra = {
      condicao: { campoId: '', operador: 'preenchido' as const, valor: '' },
      acaoEntao: { tipo: 'enviar' as const, templateId: '' },
      acaoSenao: { tipo: 'nao_enviar' as const }
    };
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const regras = acao.configuracao.regrasEmail || [];
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasEmail: [...regras, novaRegra] }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  // Regras de envio de WhatsApp (SE/ENTÃO/SENÃO)
  const atualizarRegraWhatsApp = (acaoIndex: number, regraIndex: number, novaRegra: any) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const regras = [...(acao.configuracao.regrasWhatsapp || [])];
      regras[regraIndex] = novaRegra;
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasWhatsapp: regras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const adicionarRegraWhatsApp = (acaoIndex: number) => {
    // Herda template e destinatários padrão da ação (ou primeiro template disponível)
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const templatePadrao = acao.configuracao.templateId || (whatsappTemplates.find(t => t.ativo)?.id) || (whatsappTemplates[0]?.id) || '';
      const destinatariosPadrao = acao.configuracao.destinatarios || [];
      const novaRegra = {
        condicao: { campoId: '', operador: 'preenchido' as const, valor: '' },
        acaoEntao: { tipo: 'enviar' as const, templateId: templatePadrao, destinatarios: destinatariosPadrao },
        acaoSenao: { tipo: 'nao_enviar' as const }
      };
      const regras = acao.configuracao.regrasWhatsapp || [];
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasWhatsapp: [...regras, novaRegra] }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const removerRegraWhatsApp = (acaoIndex: number, regraIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const regras = [...(acao.configuracao.regrasWhatsapp || [])];
      regras.splice(regraIndex, 1);
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasWhatsapp: regras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  // Regras de atribuir responsável (SE/ENTÃO/SENÃO)
  const adicionarRegraResponsavel = (acaoIndex: number) => {
    try {
      console.log('🟢 adicionarRegraResponsavel - ANTES:', automacao.acoes[acaoIndex]);
      
      setAutomacao(prev => {
        const novasAcoes = [...prev.acoes];
        const acao = novasAcoes[acaoIndex];
        
        // Garantir que configuracao existe
        if (!acao.configuracao) {
          acao.configuracao = {};
        }
        
        const novaRegra = {
          condicao: { campoId: '', operador: 'igual' as const, valor: '' },
          responsavelId: '',
          senao: { responsavelId: '' }
        };
        
        const regrasAtuais = Array.isArray(acao.configuracao.regrasResponsavel) ? acao.configuracao.regrasResponsavel : [];
        
        novasAcoes[acaoIndex] = {
          ...acao,
          configuracao: {
            ...acao.configuracao,
            regrasResponsavel: [...regrasAtuais, novaRegra]
          }
        };
        
        console.log('🟢 adicionarRegraResponsavel - DEPOIS:', novasAcoes[acaoIndex]);
        console.log('🟢 regrasResponsavel.length:', novasAcoes[acaoIndex].configuracao.regrasResponsavel?.length);
        
        return { ...prev, acoes: novasAcoes };
      });
    } catch (error) {
      console.error('❌ ERRO em adicionarRegraResponsavel:', error);
    }
  };

  const atualizarRegraResponsavel = (acaoIndex: number, regraIndex: number, novaRegra: any) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      
      // Garantir que configuracao existe
      if (!acao.configuracao) {
        acao.configuracao = {};
      }
      
      const regras = [...(acao.configuracao.regrasResponsavel || [])];
      regras[regraIndex] = novaRegra;
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasResponsavel: regras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const removerRegraResponsavel = (acaoIndex: number, regraIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      
      // Garantir que configuracao existe
      if (!acao.configuracao) {
        acao.configuracao = {};
      }
      
      const regras = [...(acao.configuracao.regrasResponsavel || [])];
      regras.splice(regraIndex, 1);
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasResponsavel: regras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  // Regras de atribuir API (SE/ENTÃO/SENÃO)
  const adicionarRegraApi = (acaoIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = { ...novasAcoes[acaoIndex] };
      if (!acao.configuracao) acao.configuracao = {};
      const novaRegra = {
        condicao: { campoId: '', operador: 'igual' as const, valor: '' },
        acaoEntao: { tipo: 'chamar_api' as const, url: '', metodo: 'POST' as const, campoParametroId: '', formato: 'numero-empresa' as const },
        acaoSenao: { tipo: 'chamar_api' as const, url: '', metodo: 'POST' as const, campoParametroId: '', formato: 'numero-empresa' as const }
      };
      const regrasAtuais = Array.isArray(acao.configuracao.regrasApi) ? acao.configuracao.regrasApi : [];
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasApi: [...regrasAtuais, novaRegra] }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const atualizarRegraApi = (acaoIndex: number, regraIndex: number, novaRegra: any) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      if (!acao.configuracao) acao.configuracao = {};
      const regras = [...(acao.configuracao.regrasApi || [])];
      regras[regraIndex] = novaRegra;
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasApi: regras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const removerRegraApi = (acaoIndex: number, regraIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      if (!acao.configuracao) acao.configuracao = {};
      const regras = [...(acao.configuracao.regrasApi || [])];
      regras.splice(regraIndex, 1);
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasApi: regras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  // Regras de card conectado
  const adicionarRegraCardConectado = (acaoIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = { ...novasAcoes[acaoIndex] };
      if (!acao.configuracao) acao.configuracao = {};
      const novaRegra = {
        condicao: { campoId: '', operador: 'preenchido' as const, valor: '' },
        acaoEntao: { tipo: 'criar' as const, configuracaoCardConectadoId: '' },
        acaoSenao: { tipo: 'nao_criar' as const, configuracaoCardConectadoId: '' }
      };
      const regrasAtuais = Array.isArray(acao.configuracao.regrasCardConectado) ? acao.configuracao.regrasCardConectado : [];
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasCardConectado: [...regrasAtuais, novaRegra] }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const atualizarRegraCardConectado = (acaoIndex: number, regraIndex: number, novaRegra: any) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      if (!acao.configuracao) acao.configuracao = {};
      const regras = [...(acao.configuracao.regrasCardConectado || [])];
      regras[regraIndex] = novaRegra;
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasCardConectado: regras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const removerRegraCardConectado = (acaoIndex: number, regraIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      if (!acao.configuracao) acao.configuracao = {};
      const regras = [...(acao.configuracao.regrasCardConectado || [])];
      regras.splice(regraIndex, 1);
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasCardConectado: regras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  // Regras de mover card (SE/ENTÃO/SENÃO)
  const atualizarRegraMover = (acaoIndex: number, regraIndex: number, novaRegra: any) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const regras = [...(acao.configuracao.regrasMover || [])];
      regras[regraIndex] = novaRegra;
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasMover: regras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const atualizarCondicaoEmRegraMover = (acaoIndex: number, regraIndex: number, condIndex: number, novaCondicao: any) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const regras = [...(acao.configuracao.regrasMover || [])];
      const regra = { ...regras[regraIndex] };
      const condicoesAtivas = regra.condicoes?.length > 0 ? regra.condicoes : (regra.condicao ? [regra.condicao] : []);
      const novasCondicoes = [...condicoesAtivas];
      novasCondicoes[condIndex] = novaCondicao;
      regras[regraIndex] = { ...regra, condicoes: novasCondicoes };
      novasAcoes[acaoIndex] = { ...acao, configuracao: { ...acao.configuracao, regrasMover: regras } };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const adicionarCondicaoEmRegraMover = (acaoIndex: number, regraIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const regras = [...(acao.configuracao.regrasMover || [])];
      const regra = { ...regras[regraIndex] };
      const condicoesAtivas = regra.condicoes?.length > 0 ? regra.condicoes : (regra.condicao ? [regra.condicao] : []);
      const novasCondicoes = [...condicoesAtivas, { campoId: '', operador: 'preenchido', valor: '' }];
      regras[regraIndex] = { ...regra, condicoes: novasCondicoes };
      novasAcoes[acaoIndex] = { ...acao, configuracao: { ...acao.configuracao, regrasMover: regras } };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const removerCondicaoEmRegraMover = (acaoIndex: number, regraIndex: number, condIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const regras = [...(acao.configuracao.regrasMover || [])];
      const regra = { ...regras[regraIndex] };
      const condicoesAtivas = regra.condicoes?.length > 0 ? regra.condicoes : (regra.condicao ? [regra.condicao] : []);
      const novasCondicoes = condicoesAtivas.filter((_: any, i: number) => i !== condIndex);
      regras[regraIndex] = { ...regra, condicoes: novasCondicoes.length > 0 ? novasCondicoes : [{ campoId: '', operador: 'preenchido', valor: '' }] };
      novasAcoes[acaoIndex] = { ...acao, configuracao: { ...acao.configuracao, regrasMover: regras } };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const adicionarRegraMover = (acaoIndex: number) => {
    const novaRegra = {
      condicoes: [{ campoId: '', operador: 'preenchido' as const, valor: '' }],
      acaoEntao: { tipo: 'mover' as const, etapaDestinoId: '' },
      acaoSenao: { tipo: 'nao_mover' as const }
    };
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const regras = acao.configuracao.regrasMover || [];
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasMover: [...regras, novaRegra] }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const removerRegraMover = (acaoIndex: number, regraIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const regras = (acao.configuracao.regrasMover || []).filter((_: any, i: number) => i !== regraIndex);
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasMover: regras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  const removerRegraEmail = (acaoIndex: number, regraIndex: number) => {
    setAutomacao(prev => {
      const novasAcoes = [...prev.acoes];
      const acao = novasAcoes[acaoIndex];
      const regras = (acao.configuracao.regrasEmail || []).filter((_: any, i: number) => i !== regraIndex);
      novasAcoes[acaoIndex] = {
        ...acao,
        configuracao: { ...acao.configuracao, regrasEmail: regras }
      };
      return { ...prev, acoes: novasAcoes };
    });
  };

  // Saneamento: preencher templateId vazio em regrasWhatsapp antigas
  useEffect(() => {
    setAutomacao(prev => {
      if (!prev?.acoes) return prev;
      let mudou = false;
      const novasAcoes = prev.acoes.map((acao: any) => {
        if (acao.tipo !== 'enviar_whatsapp') return acao;
        const cfg = acao.configuracao || {};
        const regras = Array.isArray(cfg.regrasWhatsapp) ? cfg.regrasWhatsapp : [];
        const templatePadrao = cfg.templateId || (whatsappTemplates.find(t => t.ativo)?.id) || (whatsappTemplates[0]?.id) || '';
        const novasRegras = regras.map((r: any) => {
          if (r?.acaoEntao?.tipo === 'enviar' && !r.acaoEntao.templateId) {
            mudou = true;
            return { ...r, acaoEntao: { ...r.acaoEntao, templateId: templatePadrao } };
          }
          return r;
        });
        if (mudou) {
          return { ...acao, configuracao: { ...cfg, regrasWhatsapp: novasRegras } };
        }
        return acao;
      });
      return mudou ? { ...prev, acoes: novasAcoes } : prev;
    });
  }, [whatsappTemplates]);

  const etapaOrigem = selectedFlow?.etapas?.find(e => e.id === automacao.etapaOrigemId);
  
  // Campos da etapa de origem + documentos habilitados como campos virtuais
  const camposEtapa = etapaOrigem?.campos || [];
  const docsHabilitados = (etapaOrigem as any)?.documentosHabilitados || [];
  const camposDocumentos = documentosTemplates
    .filter((doc: any) => docsHabilitados.includes(doc._id || doc.id))
    .map((doc: any) => ({
      id: `doc_${doc._id || doc.id}`,
      nome: `📄 ${doc.nome}`,
      tipo: 'documento',
      opcoes: [],
    }));
  const campos = [...camposEtapa, ...camposDocumentos];

  // Helper: retorna campos de uma etapa + documentos virtuais se for a etapa de origem
  const getCamposEtapa = (etapaId: string) => {
    const etapa = selectedFlow?.etapas?.find(e => e.id === etapaId);
    const camposBase = etapa?.campos || [];
    if (etapaId === automacao.etapaOrigemId) {
      return [...camposBase, ...camposDocumentos];
    }
    // Para outras etapas, incluir seus próprios documentos habilitados
    const docsEtapa = (etapa as any)?.documentosHabilitados || [];
    const camposDocsEtapa = documentosTemplates
      .filter((doc: any) => docsEtapa.includes(doc._id || doc.id))
      .map((doc: any) => ({
        id: `doc_${doc._id || doc.id}`,
        nome: `📄 ${doc.nome}`,
        tipo: 'documento',
        opcoes: [] as string[],
      }));
    return [...camposBase, ...camposDocsEtapa];
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-1 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] overflow-y-auto text-[0.8rem]">
        {/* Header Compacto */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
          <h2 className="text-sm font-bold text-gray-900">
            {automacaoExistente ? 'Editar Automação' : 'Nova Automação'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 p-0.5 rounded-lg hover:bg-gray-100"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="p-3">
          {/* Cabeçalho em grid compacto */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            {/* Nome da Automação */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nome da Automação</label>
              <input
                type="text"
                value={automacao.nome}
                onChange={(e) => setAutomacao(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Ex: Notificar equipe..."
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Seleção de Fluxo */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Fluxo</label>
              <select
                value={automacao.flowId}
                onChange={(e) => {
                  const flowId = e.target.value;
                  const flow = flows.find(f => f.id === flowId);
                  setSelectedFlow(flow || null);
                  setAutomacao(prev => ({
                    ...prev,
                    flowId,
                    etapaOrigemId: '',
                    condicoes: [],
                    acoes: []
                  }));
                }}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Selecione um fluxo</option>
                {flows.map(flow => (
                  <option key={flow.id} value={flow.id}>{flow.nome}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Controles rápidos inline */}
          <div className="flex items-center justify-between mb-3 py-1 px-2 bg-gray-50 rounded-lg">
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={(automacao as any).status !== 'inativo'}
                onChange={(e) => setAutomacao(prev => ({
                  ...prev,
                  status: e.target.checked ? 'ativo' : 'inativo'
                }))}
                className="rounded h-3 w-3"
              />
              Automação ativa
            </label>
            <span className="text-[0.65rem] px-2 py-0.5 rounded-full border"
              style={{
                color: automacao.status === 'ativo' ? '#065f46' : '#92400e',
                backgroundColor: automacao.status === 'ativo' ? '#ecfdf5' : '#fffbeb',
                borderColor: automacao.status === 'ativo' ? '#a7f3d0' : '#fed7aa'
              }}
            >
              {automacao.status === 'ativo' ? 'Ativa' : 'Inativa'}
            </span>
          </div>

          {selectedFlow && (
            <>
              {/* Tipo de Gatilho: Etapa vs Agendado */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Tipo de Gatilho</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'etapa', label: 'Quando card entra em etapa', desc: 'Dispara quando um card é criado/movido para a etapa' },
                    { id: 'agendado', label: 'Agendado (data/hora)', desc: 'Executa em um horário fixo (ex: todo dia 5 às 09:00)' },
                  ].map(opt => {
                    const selected = ((automacao as any).tipoGatilho || 'etapa') === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setAutomacao(prev => ({
                          ...prev,
                          ...( { tipoGatilho: opt.id } as any),
                          // Limpa etapaOrigemId quando muda para agendado
                          etapaOrigemId: opt.id === 'agendado' ? '' : prev.etapaOrigemId,
                          // Inicializa agendamento default quando seleciona agendado
                          ...(opt.id === 'agendado' && !(prev as any).agendamento
                            ? ({ agendamento: { frequencia: 'mensal', diaDoMes: 1, hora: 9, minuto: 0 } } as any)
                            : {}),
                        }))}
                        className={`text-left p-2 rounded-lg border-2 transition-all ${
                          selected
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/30'
                        }`}
                      >
                        <div className={`text-xs font-semibold ${selected ? 'text-indigo-700' : 'text-gray-800'}`}>{opt.label}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {((automacao as any).tipoGatilho || 'etapa') === 'agendado' ? (
                /* Configuração de Agendamento */
                <div className="mb-3 p-3 rounded-lg border border-indigo-100 bg-indigo-50/40">
                  <div className="text-xs font-semibold text-indigo-700 mb-2 flex items-center gap-1.5">
                    <ClockIconOutline className="h-3.5 w-3.5" />
                    Agendamento
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <label className="block text-[10px] font-medium text-gray-600 mb-1">Frequência</label>
                      <select
                        value={(automacao as any).agendamento?.frequencia || 'mensal'}
                        onChange={(e) => setAutomacao(prev => ({
                          ...prev,
                          ...( { agendamento: {
                            ...((prev as any).agendamento || {}),
                            frequencia: e.target.value,
                            // Garante valor inicial do dia da semana ao escolher "semanal"
                            ...(e.target.value === 'semanal' && ((prev as any).agendamento?.diaDaSemana === undefined)
                              ? { diaDaSemana: 1 } : {}),
                          } } as any),
                        }))}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                      >
                        <option value="mensal">Mensal (dia fixo)</option>
                        <option value="semanal">Semanal (dia da semana)</option>
                        <option value="diario">Diário</option>
                      </select>
                    </div>
                    {((automacao as any).agendamento?.frequencia || 'mensal') === 'mensal' && (
                      <div>
                        <label className="block text-[10px] font-medium text-gray-600 mb-1">Dia do mês</label>
                        <input
                          type="number"
                          min={1}
                          max={31}
                          value={(automacao as any).agendamento?.diaDoMes ?? 1}
                          onChange={(e) => setAutomacao(prev => ({
                            ...prev,
                            ...( { agendamento: { ...((prev as any).agendamento || {}), diaDoMes: Math.max(1, Math.min(31, parseInt(e.target.value || '1', 10))) } } as any),
                          }))}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                        />
                      </div>
                    )}
                    {((automacao as any).agendamento?.frequencia || 'mensal') === 'semanal' && (
                      <div>
                        <label className="block text-[10px] font-medium text-gray-600 mb-1">Dia da semana</label>
                        <select
                          value={(automacao as any).agendamento?.diaDaSemana ?? 1}
                          onChange={(e) => setAutomacao(prev => ({
                            ...prev,
                            ...( { agendamento: { ...((prev as any).agendamento || {}), diaDaSemana: parseInt(e.target.value, 10) } } as any),
                          }))}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                        >
                          <option value={1}>Segunda-feira</option>
                          <option value={2}>Terça-feira</option>
                          <option value={3}>Quarta-feira</option>
                          <option value={4}>Quinta-feira</option>
                          <option value={5}>Sexta-feira</option>
                          <option value={6}>Sábado</option>
                          <option value={0}>Domingo</option>
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-[10px] font-medium text-gray-600 mb-1">Hora</label>
                      <input
                        type="number"
                        min={0}
                        max={23}
                        value={(automacao as any).agendamento?.hora ?? 9}
                        onChange={(e) => setAutomacao(prev => ({
                          ...prev,
                          ...( { agendamento: { ...((prev as any).agendamento || {}), hora: Math.max(0, Math.min(23, parseInt(e.target.value || '0', 10))) } } as any),
                        }))}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-gray-600 mb-1">Minuto</label>
                      <input
                        type="number"
                        min={0}
                        max={59}
                        value={(automacao as any).agendamento?.minuto ?? 0}
                        onChange={(e) => setAutomacao(prev => ({
                          ...prev,
                          ...( { agendamento: { ...((prev as any).agendamento || {}), minuto: Math.max(0, Math.min(59, parseInt(e.target.value || '0', 10))) } } as any),
                        }))}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white"
                      />
                    </div>
                  </div>
                  <div className="text-[10px] text-gray-500 mt-2">
                    {(() => {
                      const ag = (automacao as any).agendamento || {};
                      const h = String(ag.hora ?? 9).padStart(2, '0');
                      const m = String(ag.minuto ?? 0).padStart(2, '0');
                      if (ag.frequencia === 'diario') return `⏰ Vai rodar todo dia às ${h}:${m}.`;
                      if (ag.frequencia === 'semanal') {
                        const dias = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
                        const d = dias[ag.diaDaSemana ?? 1] || 'segunda-feira';
                        return `⏰ Vai rodar toda ${d} às ${h}:${m}.`;
                      }
                      return `⏰ Vai rodar todo dia ${ag.diaDoMes ?? 1} de cada mês às ${h}:${m}.`;
                    })()}
                  </div>
                </div>
              ) : (
              /* Seleção de Etapa Origem */
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Etapa de Origem</label>
                <select
                  value={automacao.etapaOrigemId}
                  onChange={(e) => setAutomacao(prev => ({
                    ...prev,
                    etapaOrigemId: e.target.value,
                    condicoes: []
                  }))}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Selecione a etapa de origem</option>
                  {selectedFlow.etapas?.map(etapa => (
                    <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>
                  ))}
                </select>
              </div>
              )}

              {/* Configuração de Ações */}
              {(automacao.etapaOrigemId || ((automacao as any).tipoGatilho === 'agendado')) && (
                <>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700">Ações da Automação</label>
                      <p className="text-[0.65rem] text-gray-500">Configure as ações que serão executadas quando as condições forem atendidas</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className="text-[0.65rem] bg-gray-100 text-gray-700 px-2 py-0.5 rounded hover:bg-gray-200 flex items-center gap-1"
                      >
                        <CogIcon className="h-3 w-3" />
                        Opções Avançadas
                      </button>
                      <button
                        onClick={() => setPreviewMode(!previewMode)}
                        className="text-[0.65rem] bg-blue-100 text-blue-700 px-2 py-0.5 rounded hover:bg-blue-200 flex items-center gap-1"
                      >
                        {previewMode ? <EyeSlashIcon className="h-3 w-3" /> : <EyeIcon className="h-3 w-3" />}
                        Visualizar
                      </button>
                    </div>
                  </div>

                  {/* Botões para adicionar ações - Compactos */}
                  <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-1.5 mb-3">
                    <button
                      onClick={() => adicionarAcao('mover_card')}
                      className="flex items-center justify-center gap-1 p-2 border-2 border-dashed border-indigo-200 text-indigo-600 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                    >
                      <ArrowRightIcon className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">Mover Card</span>
                    </button>
                    <button
                      onClick={() => adicionarAcao('enviar_email')}
                      className="flex items-center justify-center gap-1 p-2 border-2 border-dashed border-indigo-200 text-indigo-600 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                    >
                      <EnvelopeIcon className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">Enviar E-mail</span>
                    </button>
                    <button
                      onClick={() => adicionarAcao('enviar_whatsapp')}
                      className="flex items-center justify-center gap-1 p-2 border-2 border-dashed border-indigo-200 text-indigo-600 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                    >
                      <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">Enviar WhatsApp</span>
                    </button>
                    <button
                      onClick={() => adicionarAcao('atribuir_responsavel')}
                      className="flex items-center justify-center gap-1 p-2 border-2 border-dashed border-indigo-200 text-indigo-600 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-xs font-medium">Atribuir Responsável</span>
                    </button>
                    <button
                      onClick={() => adicionarAcao('exibir_ocultar')}
                      className="flex items-center justify-center gap-1 p-2 border-2 border-dashed border-indigo-200 text-indigo-600 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                    >
                      <EyeIcon className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">Exibir/Ocultar</span>
                    </button>
                    <button
                      onClick={() => adicionarAcao('criar_card_conectado')}
                      className="flex items-center justify-center gap-1 p-2 border-2 border-dashed border-indigo-200 text-indigo-600 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                    >
                      <LinkIcon className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">Criar Conectado</span>
                    </button>
                    <button
                      onClick={() => adicionarAcao('mover_card_conectado')}
                      className="flex items-center justify-center gap-1 p-2 border-2 border-dashed border-indigo-200 text-indigo-600 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                    >
                      <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">Mover Conectado</span>
                    </button>
                    <button
                      onClick={() => adicionarAcao('atribuir_api')}
                      className="flex items-center justify-center gap-1 p-2 border-2 border-dashed border-indigo-200 text-indigo-600 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className="text-xs font-medium">Atribuir API</span>
                    </button>
                    <button
                      onClick={() => adicionarAcao('atualizar_campo')}
                      className="flex items-center justify-center gap-1 p-2 border-2 border-dashed border-teal-200 text-teal-600 rounded-lg hover:border-teal-400 hover:bg-teal-50 transition-colors"
                      title="Grava um valor fixo em campos do card quando ele entra na etapa"
                    >
                      <PencilSquareIcon className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">Atualizar Campo</span>
                    </button>
                    <button
                      onClick={() => adicionarAcao('criar_card_template')}
                      className="flex items-center justify-center gap-1 p-2 border-2 border-dashed border-violet-200 text-violet-600 rounded-lg hover:border-violet-400 hover:bg-violet-50 transition-colors"
                      title="Cria um card a partir de um modelo (Card Personalizado) configurado em uma etapa"
                    >
                      <Squares2X2Icon className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">Criar Card de Modelo</span>
                    </button>
                  </div>

                  {/* 🔁 Aplicar campo em todas as ações Criar Card de Modelo */}
                  {(() => {
                    const acoesTemplate = automacao.acoes.filter((a: any) => a.tipo === 'criar_card_template');
                    if (acoesTemplate.length < 2) return null;

                    // Campos disponíveis: união dos campos das etapas-destino de cada ação
                    const camposMap = new Map<string, { id: string; nome: string; tipo: string }>();
                    acoesTemplate.forEach((a: any) => {
                      const c = a.configuracao?.criarCardTemplate || {};
                      const fluxoDst = flows.find((f: any) => f.id === c.fluxoDestinoId);
                      const etapaDst = fluxoDst?.etapas?.find((e: any) => e.id === c.etapaDestinoId);
                      etapaDst?.campos?.forEach((campo: any) => {
                        if (campo?.id && !camposMap.has(campo.id)) {
                          camposMap.set(campo.id, { id: campo.id, nome: campo.nome || campo.id, tipo: campo.tipo || 'texto' });
                        }
                      });
                    });
                    const camposLista = Array.from(camposMap.values());

                    const aplicarBulk = () => {
                      if (!bulkCampoId) {
                        alert('Selecione um campo.');
                        return;
                      }
                      let afetadas = 0;
                      setAutomacao(prev => ({
                        ...prev,
                        acoes: prev.acoes.map((a: any) => {
                          if (a.tipo !== 'criar_card_template') return a;
                          const cfg = a.configuracao?.criarCardTemplate || {};
                          const fluxoDst = flows.find((f: any) => f.id === cfg.fluxoDestinoId);
                          const etapaDst = fluxoDst?.etapas?.find((e: any) => e.id === cfg.etapaDestinoId);
                          const temCampo = etapaDst?.campos?.some((c: any) => c.id === bulkCampoId);
                          if (!temCampo) return a;
                          const camposExtras: any[] = Array.isArray(cfg.camposExtras) ? cfg.camposExtras : [];
                          const idx = camposExtras.findIndex((c: any) => c.campoId === bulkCampoId);
                          const novoArr = idx >= 0
                            ? camposExtras.map((c: any, i: number) => i === idx ? { ...c, valor: bulkValor } : c)
                            : [...camposExtras, { campoId: bulkCampoId, valor: bulkValor }];
                          afetadas++;
                          return {
                            ...a,
                            configuracao: {
                              ...a.configuracao,
                              criarCardTemplate: { ...cfg, camposExtras: novoArr },
                            },
                          };
                        }),
                      }));
                      setTimeout(() => alert(`Aplicado em ${afetadas} de ${acoesTemplate.length} ações.`), 0);
                    };

                    return (
                      <div className="mb-3 border border-violet-200 bg-violet-50 rounded-lg">
                        <button
                          type="button"
                          onClick={() => setBulkPanelAberto(v => !v)}
                          className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-violet-700 hover:bg-violet-100 rounded-lg"
                        >
                          <span>🔁 Aplicar campo em todas as {acoesTemplate.length} ações "Criar Card de Modelo"</span>
                          <span>{bulkPanelAberto ? '▲' : '▼'}</span>
                        </button>
                        {bulkPanelAberto && (
                          <div className="px-3 pb-3 pt-1 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                            <div>
                              <label className="block text-[10px] text-violet-700 mb-1">Campo</label>
                              <select
                                value={bulkCampoId}
                                onChange={(e) => setBulkCampoId(e.target.value)}
                                className="w-full px-2 py-1.5 text-xs border border-violet-300 rounded bg-white"
                              >
                                <option value="">Selecione...</option>
                                {camposLista.map(c => (
                                  <option key={c.id} value={c.id}>{c.nome} ({c.tipo})</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] text-violet-700 mb-1">
                                Valor (suporta placeholders: {'{{hoje}}'}, {'{{hoje+30d}}'}, {'{{dia_mes:1}}'}, {'{{ultimo_dia_mes}}'}, etc.)
                              </label>
                              <input
                                type="text"
                                value={bulkValor}
                                onChange={(e) => setBulkValor(e.target.value)}
                                placeholder="Ex: {{dia_mes:1}}"
                                className="w-full px-2 py-1.5 text-xs border border-violet-300 rounded bg-white"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={aplicarBulk}
                              className="px-3 py-1.5 text-xs font-medium bg-violet-600 text-white rounded hover:bg-violet-700"
                            >
                              Aplicar a todas
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Lista de Ações - Compacta */}
                  <div className="space-y-2">
                    {automacao.acoes.map((acao, acaoIndex) => {
                      const isMin = acoesMinimizadas.has(acaoIndex);
                      // Resumo do que foi configurado, mostrado no header da ação
                      const getResumoAcao = (): string | null => {
                        try {
                          const cfg: any = acao.configuracao || {};
                          if (acao.tipo === 'mover_card') {
                            const etapa = selectedFlow?.etapas?.find((e: any) => e.id === cfg.etapaDestinoId);
                            const partes: string[] = [];
                            if (etapa) partes.push(`→ ${etapa.nome}`);
                            const nRegras = Array.isArray(cfg.regrasMover) ? cfg.regrasMover.length : 0;
                            if (nRegras > 0) partes.push(`${nRegras} regra${nRegras > 1 ? 's' : ''} condicional${nRegras > 1 ? 'is' : ''}`);
                            return partes.length ? partes.join(' • ') : null;
                          }
                          if (acao.tipo === 'enviar_email') {
                            const tpl = templates.find((t: any) => String(t.id) === String(cfg.templateId));
                            const partes: string[] = [];
                            if (tpl) partes.push(`Template: ${tpl.nome || tpl.assunto || tpl.id}`);
                            const dest = Array.isArray(cfg.destinatarios) ? cfg.destinatarios.length : 0;
                            if (dest > 0) partes.push(`${dest} destinatário${dest > 1 ? 's' : ''}`);
                            const nRegras = Array.isArray(cfg.regrasEmail) ? cfg.regrasEmail.length : 0;
                            if (nRegras > 0) partes.push(`${nRegras} regra${nRegras > 1 ? 's' : ''}`);
                            return partes.length ? partes.join(' • ') : null;
                          }
                          if (acao.tipo === 'enviar_whatsapp') {
                            const tpl = whatsappTemplates.find((t: any) => String(t.id) === String(cfg.templateId));
                            const partes: string[] = [];
                            if (tpl) partes.push(`Template: ${tpl.nome || tpl.id}`);
                            const dest = Array.isArray(cfg.destinatarios) ? cfg.destinatarios.length : 0;
                            if (dest > 0) partes.push(`${dest} destinatário${dest > 1 ? 's' : ''}`);
                            const nRegras = Array.isArray(cfg.regrasWhatsapp) ? cfg.regrasWhatsapp.length : 0;
                            if (nRegras > 0) partes.push(`${nRegras} regra${nRegras > 1 ? 's' : ''}`);
                            return partes.length ? partes.join(' • ') : null;
                          }
                          if (acao.tipo === 'exibir_ocultar') {
                            const nRegras = Array.isArray(cfg.regras) ? cfg.regras.length : 0;
                            return nRegras > 0 ? `${nRegras} regra${nRegras > 1 ? 's' : ''} de visibilidade` : null;
                          }
                          if (acao.tipo === 'atribuir_responsavel') {
                            const nRegras = Array.isArray(cfg.regrasResponsavel) ? cfg.regrasResponsavel.length : 0;
                            return nRegras > 0 ? `${nRegras} regra${nRegras > 1 ? 's' : ''} de atribuição` : null;
                          }
                          if (acao.tipo === 'atribuir_api') {
                            const nRegras = Array.isArray(cfg.regrasApi) ? cfg.regrasApi.length : 0;
                            return nRegras > 0 ? `${nRegras} chamada${nRegras > 1 ? 's' : ''} de API configurada${nRegras > 1 ? 's' : ''}` : null;
                          }
                          if (acao.tipo === 'criar_card_conectado') {
                            const nRegras = Array.isArray(cfg.regrasCardConectado) ? cfg.regrasCardConectado.length : 0;
                            const partes: string[] = [];
                            if (cfg.configuracaoCardConectadoId) partes.push('Configuração direta');
                            if (nRegras > 0) partes.push(`${nRegras} regra${nRegras > 1 ? 's' : ''}`);
                            return partes.length ? partes.join(' • ') : null;
                          }
                          if (acao.tipo === 'mover_card_conectado') {
                            const fluxo = flows.find((f: any) => f.id === cfg.fluxoDestinoId);
                            const etapa = fluxo?.etapas?.find((e: any) => e.id === cfg.etapaDestinoId);
                            const partes: string[] = [];
                            if (fluxo) partes.push(fluxo.nome);
                            if (etapa) partes.push(`→ ${etapa.nome}`);
                            return partes.length ? partes.join(' ') : null;
                          }
                          if (acao.tipo === 'criar_card_template') {
                            const c = cfg.criarCardTemplate || {};
                            const fluxo: any = flows.find((f: any) => f.id === c.fluxoDestinoId);
                            const etapa: any = fluxo?.etapas?.find((e: any) => e.id === c.etapaDestinoId);
                            const tpl: any = etapa?.cardsPersonalizados?.find((t: any) => t.id === c.templateId);
                            const resp: any = colaboradores.find((co: any) => (co.id || co._id) === c.responsavelId);
                            const partes: string[] = [];
                            if (fluxo) partes.push(fluxo.nome);
                            if (etapa) partes.push(`→ ${etapa.nome}`);
                            if (tpl) partes.push(`Modelo: ${tpl.nome}`);
                            if (resp) partes.push(`👤 ${resp.nome}`);
                            if (c.tituloCustom) partes.push(`"${c.tituloCustom}"`);
                            return partes.length ? partes.join(' • ') : null;
                          }
                          return null;
                        } catch {
                          return null;
                        }
                      };
                      const resumoAcao = getResumoAcao();
                      return (
                      <div key={acaoIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div
                          className="bg-gray-50 px-3 py-2 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => toggleAcaoMinimizada(acaoIndex)}
                          title={isMin ? 'Expandir ação' : 'Minimizar ação'}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <ChevronRightIcon className={`h-3.5 w-3.5 text-gray-500 transition-transform ${isMin ? '' : 'rotate-90'}`} />
                              {acao.tipo === 'mover_card' && <ArrowRightIcon className="h-4 w-4 text-green-600" />}
                              {acao.tipo === 'enviar_email' && <EnvelopeIcon className="h-4 w-4 text-purple-600" />}
                              {acao.tipo === 'enviar_whatsapp' && <ChatBubbleLeftRightIcon className="h-4 w-4 text-emerald-600" />}
                              {acao.tipo === 'exibir_ocultar' && <EyeIcon className="h-4 w-4 text-blue-600" />}
                              {acao.tipo === 'atribuir_responsavel' && <UserIcon className="h-4 w-4 text-indigo-600" />}
                              {acao.tipo === 'criar_card_conectado' && <LinkIcon className="h-4 w-4 text-violet-600" />}
                              {acao.tipo === 'mover_card_conectado' && <ArrowsRightLeftIcon className="h-4 w-4 text-amber-600" />}
                              {acao.tipo === 'atribuir_api' && (
                                <svg className="h-4 w-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                              )}
                              {acao.tipo === 'criar_card_template' && <Squares2X2Icon className="h-4 w-4 text-violet-600" />}
                              {acao.tipo === 'atualizar_campo' && <PencilSquareIcon className="h-4 w-4 text-teal-600" />}
                              <div>
                                <h4 className="text-xs font-medium text-gray-900">
                                  Ação {acaoIndex + 1}: {
                                    acao.tipo === 'mover_card' ? 'Mover Card' : 
                                    acao.tipo === 'enviar_email' ? 'Enviar E-mail' : 
                                    acao.tipo === 'enviar_whatsapp' ? 'Enviar WhatsApp' : 
                                    acao.tipo === 'exibir_ocultar' ? 'Exibir/Ocultar Campo' :
                                    acao.tipo === 'criar_card_conectado' ? 'Criar Card Conectado' :
                                    acao.tipo === 'mover_card_conectado' ? 'Mover Card Conectado' :
                                    acao.tipo === 'atribuir_api' ? 'Atribuir API' :
                                    acao.tipo === 'criar_card_template' ? 'Criar Card de Modelo' :
                                    acao.tipo === 'atualizar_campo' ? 'Atualizar Campo' :
                                    'Atribuir Responsável'
                                  }
                                </h4>
                                <p className="text-[0.65rem] text-gray-500">
                                  {acao.tipo === 'mover_card' ? 'Move o card para outra etapa' : 
                                   acao.tipo === 'enviar_email' ? 'Envia um e-mail usando template' : 
                                   acao.tipo === 'enviar_whatsapp' ? 'Envia uma mensagem WhatsApp usando template' : 
                                   acao.tipo === 'exibir_ocultar' ? 'Controla a visibilidade de campos' :
                                   acao.tipo === 'criar_card_conectado' ? 'Cria um card conectado usando configuração salva' :
                                   acao.tipo === 'mover_card_conectado' ? 'Move um card conectado existente para outra etapa/fluxo' :
                                   acao.tipo === 'atribuir_api' ? 'Chama uma API externa quando a condição é atendida' :
                                   acao.tipo === 'criar_card_template' ? 'Cria um card a partir de um modelo de card personalizado' :
                                   acao.tipo === 'atualizar_campo' ? 'Grava um valor fixo em campos do card ao entrar na etapa' :
                                   'Atribui responsável automaticamente'}
                                </p>
                                {resumoAcao && (
                                  <p className="text-[0.7rem] text-indigo-700 font-medium mt-0.5 truncate max-w-[700px]" title={resumoAcao}>
                                    {resumoAcao}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); duplicarAcao(acaoIndex); }}
                                className="text-indigo-500 hover:text-indigo-700 p-1 rounded hover:bg-indigo-50"
                                title="Duplicar ação"
                              >
                                <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); removerAcao(acaoIndex); }}
                                className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                title="Remover ação"
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>

                        {!isMin && (
                        <div className="p-3">
                          {/* Configuração específica para Mover Card */}
                          {acao.tipo === 'atualizar_campo' && (() => {
                            const camposAlvo = getCamposEtapa(automacao.etapaOrigemId);
                            const linhas = (acao.configuracao as any).campos || [];

                            const setLinhas = (novas: any[]) => atualizarAcao(acaoIndex, {
                              ...acao,
                              configuracao: { ...acao.configuracao, campos: novas }
                            });

                            return (
                              <div className="space-y-2">
                                <p className="text-xs text-gray-500">
                                  Quando o card entrar na etapa "<strong>{etapaOrigem?.nome || '...'}</strong>",
                                  estes campos recebem o valor abaixo automaticamente.
                                </p>

                                {linhas.length === 0 && (
                                  <p className="text-xs text-gray-400 italic">Nenhum campo configurado ainda.</p>
                                )}

                                {linhas.map((linha: any, i: number) => {
                                  const campoSel = camposAlvo.find((c: any) => c.id === linha.campoId);
                                  const opcoes = (campoSel as any)?.opcoes || [];
                                  return (
                                    <div key={i} className="flex items-center gap-2">
                                      <select
                                        value={linha.campoId || ''}
                                        onChange={(e) => setLinhas(linhas.map((l: any, j: number) =>
                                          // troca de campo zera o valor: as opções do campo novo são outras
                                          j === i ? { campoId: e.target.value, valor: '' } : l
                                        ))}
                                        className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                      >
                                        <option value="">Selecione o campo</option>
                                        {camposAlvo.map((c: any) => (
                                          <option key={c.id} value={c.id}>{c.nome}</option>
                                        ))}
                                      </select>

                                      <span className="text-xs text-gray-400">=</span>

                                      {opcoes.length > 0 ? (
                                        <select
                                          value={linha.valor || ''}
                                          onChange={(e) => setLinhas(linhas.map((l: any, j: number) =>
                                            j === i ? { ...l, valor: e.target.value } : l
                                          ))}
                                          className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        >
                                          <option value="">Selecione o valor</option>
                                          {opcoes.map((op: string) => (
                                            <option key={op} value={op}>{op}</option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          value={linha.valor || ''}
                                          onChange={(e) => setLinhas(linhas.map((l: any, j: number) =>
                                            j === i ? { ...l, valor: e.target.value } : l
                                          ))}
                                          placeholder="Valor a gravar"
                                          className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        />
                                      )}

                                      <button
                                        onClick={() => setLinhas(linhas.filter((_: any, j: number) => j !== i))}
                                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Remover campo"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                  );
                                })}

                                <button
                                  onClick={() => setLinhas([...linhas, { campoId: '', valor: '' }])}
                                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-600 border-2 border-dashed border-indigo-200 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                                >
                                  <PlusIcon className="h-3.5 w-3.5" />
                                  Adicionar campo
                                </button>
                              </div>
                            );
                          })()}

                          {acao.tipo === 'mover_card' && (
                            <div className="space-y-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Etapa de Destino</label>
                                <select
                                  value={acao.configuracao.etapaDestinoId || ''}
                                  onChange={(e) => atualizarAcao(acaoIndex, {
                                    ...acao,
                                    configuracao: { ...acao.configuracao, etapaDestinoId: e.target.value }
                                  })}
                                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                >
                                  <option value="">Selecione a etapa de destino</option>
                                  {selectedFlow.etapas
                                    ?.filter(etapa => etapa.id !== automacao.etapaOrigemId)
                                    .map(etapa => (
                                      <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>
                                    ))}
                                </select>
                              </div>
                              {showAdvanced && (
                                <div className="bg-gray-50 p-2 rounded-lg">
                                  <label className="flex items-center gap-2 text-xs">
                                    <input type="checkbox" className="rounded" />
                                    Notificar responsáveis sobre a movimentação
                                  </label>
                                </div>
                              )}

                              {/* Regras condicionais para mover card */}
                              {(!acao.configuracao.regrasMover || acao.configuracao.regrasMover.length === 0) ? (
                                <div className="bg-indigo-50 p-3 rounded-lg text-center">
                                  <h4 className="text-sm font-medium text-indigo-800 mb-2">Regras para Mover Card</h4>
                                  <p className="text-xs text-indigo-700 mb-3">
                                    Opcional: mova o card para uma etapa baseada em condições (SE/ENTÃO/SENÃO)
                                  </p>
                                  <button
                                    onClick={() => adicionarRegraMover(acaoIndex)}
                                    className="bg-indigo-600 text-white px-4 py-2 rounded text-sm hover:bg-indigo-700 inline-flex items-center gap-2"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    Adicionar Regra de Movimento
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-medium text-gray-800">Regras de Movimento ({acao.configuracao.regrasMover.length})</h4>
                                    <button
                                      onClick={() => adicionarRegraMover(acaoIndex)}
                                      className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700 flex items-center gap-1"
                                    >
                                      <PlusIcon className="h-3 w-3" /> Nova Regra
                                    </button>
                                  </div>

                                  <div className="space-y-3">
                                    {acao.configuracao.regrasMover.map((regra: any, regraIndex: number) => (
                                      <div key={regraIndex} className="border border-gray-200 p-4 rounded-lg bg-white">
                                        <div className="flex items-center justify-between mb-3">
                                          <span className="text-xs text-gray-500">Regra #{regraIndex + 1}</span>
                                          <button
                                            onClick={() => removerRegraMover(acaoIndex, regraIndex)}
                                            className="text-red-500 hover:text-red-600 text-xs flex items-center gap-1"
                                          >
                                            <TrashIcon className="h-4 w-4" /> Remover
                                          </button>
                                        </div>

                                        {/* Condição SE */}
                                        <div className="mb-3 p-3 rounded-lg bg-gray-50">
                                          <label className="block text-xs font-medium text-gray-700 mb-2">SE (Condição)</label>

                                          {/* Loop sobre condições (suporte a múltiplas com E) */}
                                          {(regra.condicoes?.length > 0 ? regra.condicoes : (regra.condicao ? [regra.condicao] : [{ campoId: '', operador: 'preenchido', valor: '' }])).map((cond: any, condIndex: number) => (
                                            <div key={condIndex}>
                                              {condIndex > 0 && (
                                                <div className="flex items-center my-2">
                                                  <div className="flex-1 h-px bg-gray-300" />
                                                  <span className="mx-2 text-xs font-bold text-indigo-700 bg-indigo-100 border border-indigo-200 px-2 py-0.5 rounded-full">E</span>
                                                  <div className="flex-1 h-px bg-gray-300" />
                                                </div>
                                              )}
                                              <div className="bg-white rounded-lg border border-gray-200 p-3">
                                                {/* Trash por condição (quando há mais de uma) */}
                                                {(regra.condicoes?.length > 1) && (
                                                  <div className="flex justify-end mb-1">
                                                    <button
                                                      type="button"
                                                      onClick={() => removerCondicaoEmRegraMover(acaoIndex, regraIndex, condIndex)}
                                                      className="text-red-400 hover:text-red-600 p-0.5 rounded"
                                                      title="Remover esta condição"
                                                    >
                                                      <TrashIcon className="h-3.5 w-3.5" />
                                                    </button>
                                                  </div>
                                                )}

                                                {/* Seletor de Etapa */}
                                                <div className="mb-2">
                                                  <label className="block text-xs text-gray-600 mb-1">🔀 Buscar campo da etapa:</label>
                                                  <select
                                                    value={cond.etapaOrigemCampoId || automacao.etapaOrigemId}
                                                    onChange={(e) => atualizarCondicaoEmRegraMover(acaoIndex, regraIndex, condIndex, { ...cond, etapaOrigemCampoId: e.target.value, campoId: '' })}
                                                    className="w-full px-3 py-2 border border-blue-300 rounded-lg bg-blue-50 text-sm"
                                                  >
                                                    <option value={automacao.etapaOrigemId}>
                                                      ✓ {selectedFlow?.etapas?.find(e => e.id === automacao.etapaOrigemId)?.nome || 'Etapa atual'}
                                                    </option>
                                                    {selectedFlow?.etapas?.filter(e => e.id !== automacao.etapaOrigemId).map(etapa => (
                                                      <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>
                                                    ))}
                                                  </select>
                                                </div>

                                                {/* Campo / Operador / Valor */}
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                  <div>
                                                    <label className="block text-xs text-gray-600 mb-1">Campo</label>
                                                    <select
                                                      value={cond.campoId || ''}
                                                      onChange={(e) => atualizarCondicaoEmRegraMover(acaoIndex, regraIndex, condIndex, { ...cond, campoId: e.target.value })}
                                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    >
                                                      <option value="">Selecione um campo</option>
                                                      {getCamposEtapa(cond.etapaOrigemCampoId || automacao.etapaOrigemId).map(c => (
                                                        <option key={c.id} value={c.id}>{c.nome}</option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                  <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Operador</label>
                                                    <select
                                                      value={cond.operador || 'preenchido'}
                                                      onChange={(e) => atualizarCondicaoEmRegraMover(acaoIndex, regraIndex, condIndex, { ...cond, operador: e.target.value })}
                                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    >
                                                      <option value="preenchido">está preenchido</option>
                                                      <option value="vazio">está vazio</option>
                                                      <option value="igual">é igual a</option>
                                                      <option value="diferente">é diferente de</option>
                                                      <option value="contem">contém</option>
                                                      <option value="nao_contem">não contém</option>
                                                      <option value="maior">é maior que</option>
                                                      <option value="menor">é menor que</option>
                                                      <option value="maior_igual">é igual ou maior que</option>
                                                      <option value="dias_restantes">faltam X dias ou menos</option>
                                                    </select>
                                                  </div>
                                                  <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Valor</label>
                                                    {(() => {
                                                      const campoSel = getCamposEtapa(cond.etapaOrigemCampoId || automacao.etapaOrigemId).find(c => c.id === (cond.campoId || ''));
                                                      const op = cond.operador;
                                                      const precisaValor = !['preenchido', 'vazio', 'maior_igual'].includes(op || '');
                                                      if (!precisaValor) {
                                                        return (
                                                          <input type="text" value="" disabled className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-400" placeholder="Não aplicável para este operador" />
                                                        );
                                                      }
                                                      if (op === 'dias_restantes') {
                                                        return (
                                                          <input type="number" min="1" value={String(cond.valor ?? '')}
                                                            onChange={(e) => atualizarCondicaoEmRegraMover(acaoIndex, regraIndex, condIndex, { ...cond, valor: e.target.value })}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Ex: 30 (dias)" />
                                                        );
                                                      }
                                                      if (campoSel?.opcoes && campoSel.opcoes.length > 0) {
                                                        return (
                                                          <select value={String(cond.valor ?? '')}
                                                            onChange={(e) => atualizarCondicaoEmRegraMover(acaoIndex, regraIndex, condIndex, { ...cond, valor: e.target.value })}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                                                            <option value="">Escolher...</option>
                                                            {(campoSel.opcoes || []).map((opt: string) => (
                                                              <option key={opt} value={opt}>{opt}</option>
                                                            ))}
                                                          </select>
                                                        );
                                                      }
                                                      if (campoSel?.tipo === 'valor') {
                                                        return (
                                                          <input type="number" value={String(cond.valor ?? '')}
                                                            onChange={(e) => atualizarCondicaoEmRegraMover(acaoIndex, regraIndex, condIndex, { ...cond, valor: e.target.value })}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Digite um número" />
                                                        );
                                                      }
                                                      if (campoSel?.tipo === 'data' || campoSel?.tipo === 'data_vencimento') {
                                                        return (
                                                          <input type={campoSel.tipo === 'data_vencimento' ? 'datetime-local' : 'date'}
                                                            value={String(cond.valor ?? '')}
                                                            onChange={(e) => atualizarCondicaoEmRegraMover(acaoIndex, regraIndex, condIndex, { ...cond, valor: e.target.value })}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                                                        );
                                                      }
                                                      return (
                                                        <input type="text" value={String(cond.valor ?? '')}
                                                          onChange={(e) => atualizarCondicaoEmRegraMover(acaoIndex, regraIndex, condIndex, { ...cond, valor: e.target.value })}
                                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Digite o valor" />
                                                      );
                                                    })()}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          ))}

                                          {/* Botão + Adicionar condição E */}
                                          <button
                                            type="button"
                                            onClick={() => adicionarCondicaoEmRegraMover(acaoIndex, regraIndex)}
                                            className="mt-2 text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1 font-medium"
                                          >
                                            <PlusIcon className="h-3.5 w-3.5" />
                                            Adicionar condição <span className="font-bold bg-indigo-100 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded-full ml-0.5">E</span>
                                          </button>
                                        </div>

                                        {/* ENTÃO */}
                                        <div className="mt-3 p-3 rounded-lg bg-indigo-50">
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                                            <div>
                                              <label className="block text-xs font-medium text-gray-700 mb-1">ENTÃO</label>
                                              <select
                                                value={regra.acaoEntao?.tipo || 'mover'}
                                                onChange={(e) => atualizarRegraMover(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  acaoEntao: { ...regra.acaoEntao, tipo: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                              >
                                                <option value="mover">mover</option>
                                                <option value="nao_mover">não mover</option>
                                              </select>
                                            </div>
                                            {regra.acaoEntao?.tipo === 'mover' && (
                                              <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Etapa de Destino</label>
                                                <select
                                                  value={regra.acaoEntao?.etapaDestinoId || ''}
                                                  onChange={(e) => atualizarRegraMover(acaoIndex, regraIndex, {
                                                    ...regra,
                                                    acaoEntao: { ...regra.acaoEntao, etapaDestinoId: e.target.value }
                                                  })}
                                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                >
                                                  <option value="">Escolher...</option>
                                                  {selectedFlow.etapas
                                                    ?.filter(etapa => etapa.id !== automacao.etapaOrigemId)
                                                    .map(etapa => (
                                                      <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>
                                                    ))}
                                                </select>
                                              </div>
                                            )}
                                          </div>
                                        </div>

                                        {/* SENÃO */}
                                        <div className="mt-3 p-3 rounded-lg bg-yellow-50">
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                                            <div>
                                              <label className="block text-xs font-medium text-gray-700 mb-1">SENÃO</label>
                                              <select
                                                value={regra.acaoSenao?.tipo || 'nao_mover'}
                                                onChange={(e) => atualizarRegraMover(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  acaoSenao: { ...regra.acaoSenao, tipo: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                              >
                                                <option value="nao_mover">não mover</option>
                                                <option value="mover">mover</option>
                                              </select>
                                            </div>
                                            {regra.acaoSenao?.tipo === 'mover' && (
                                              <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Etapa de Destino</label>
                                                <select
                                                  value={regra.acaoSenao?.etapaDestinoId || ''}
                                                  onChange={(e) => atualizarRegraMover(acaoIndex, regraIndex, {
                                                    ...regra,
                                                    acaoSenao: { ...regra.acaoSenao, etapaDestinoId: e.target.value }
                                                  })}
                                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                >
                                                  <option value="">Escolher...</option>
                                                  {selectedFlow.etapas
                                                    ?.filter(etapa => etapa.id !== automacao.etapaOrigemId)
                                                    .map(etapa => (
                                                      <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>
                                                    ))}
                                                </select>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Configuração específica para Enviar E-mail */}
                          {acao.tipo === 'enviar_email' && (
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Template de E-mail
                                </label>
                                <select
                                  value={acao.configuracao.templateId || ''}
                                  onChange={(e) => atualizarAcao(acaoIndex, {
                                    ...acao,
                                    configuracao: { ...acao.configuracao, templateId: e.target.value }
                                  })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                  disabled={loadingTemplates}
                                >
                                  <option value="">
                                    {loadingTemplates ? 'Carregando templates...' : 'Selecione um template'}
                                  </option>
                                  {templates.map(template => (
                                    <option key={template.id} value={template.id}>
                                      {template.nome} - {template.assunto}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              
                              {showAdvanced && (
                                <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Destinatários Adicionais
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="email1@empresa.com, email2@empresa.com"
                                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <label className="flex items-center gap-2 text-sm">
                                      <input type="checkbox" className="rounded" />
                                      Enviar cópia para responsável
                                    </label>
                                    <label className="flex items-center gap-2 text-sm">
                                      <input type="checkbox" className="rounded" />
                                      Agendar envio
                                    </label>
                                  </div>
                                </div>
                              )}

                              {/* Regras condicionais de envio (SE/ENTÃO/SENÃO) */}
                              {(!acao.configuracao.regrasEmail || acao.configuracao.regrasEmail.length === 0) ? (
                                <div className="bg-indigo-50 p-3 rounded-lg text-center">
                                  <h4 className="text-sm font-medium text-indigo-800 mb-2">Regras de Envio Condicional</h4>
                                  <p className="text-xs text-indigo-700 mb-3">
                                    Opcional: defina condições para enviar (ou não) este e-mail
                                  </p>
                                  <button
                                    onClick={() => adicionarRegraEmail(acaoIndex)}
                                    className="bg-indigo-600 text-white px-4 py-2 rounded text-sm hover:bg-indigo-700 inline-flex items-center gap-2"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    Adicionar Regra de Envio
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-medium text-gray-800">Regras de Envio ({acao.configuracao.regrasEmail.length})</h4>
                                    <button
                                      onClick={() => adicionarRegraEmail(acaoIndex)}
                                      className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700 flex items-center gap-1"
                                    >
                                      <PlusIcon className="h-3 w-3" />
                                      Nova Regra
                                    </button>
                                  </div>

                                  <div className="space-y-3">
                                    {acao.configuracao.regrasEmail.map((regra: any, regraIndex: number) => (
                                      <div key={regraIndex} className="border border-gray-200 p-4 rounded-lg bg-white">
                                        <div className="flex items-center justify-between mb-3">
                                          <span className="text-xs text-gray-500">Regra #{regraIndex + 1}</span>
                                          <button
                                            onClick={() => removerRegraEmail(acaoIndex, regraIndex)}
                                            className="text-red-500 hover:text-red-600 text-xs flex items-center gap-1"
                                          >
                                            <TrashIcon className="h-4 w-4" /> Remover
                                          </button>
                                        </div>

                                        {/* Condição SE */}
                                        <div className="mb-3 p-3 rounded-lg bg-gray-50">
                                          <label className="block text-xs font-medium text-gray-700 mb-2">SE (Condição)</label>
                                          
                                          {/* Seletor de Etapa */}
                                          <div className="mb-3">
                                            <label className="block text-xs text-gray-600 mb-1">
                                              🔀 Buscar campo da etapa:
                                            </label>
                                            <select
                                              value={regra.condicao?.etapaOrigemCampoId || automacao.etapaOrigemId}
                                              onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                ...regra,
                                                condicao: { ...regra.condicao, etapaOrigemCampoId: e.target.value, campoId: '' }
                                              })}
                                              className="w-full px-3 py-2 border border-blue-300 rounded-lg bg-blue-50 text-sm"
                                            >
                                              <option value={automacao.etapaOrigemId}>
                                                ✓ {selectedFlow?.etapas?.find(e => e.id === automacao.etapaOrigemId)?.nome || 'Etapa atual'}
                                              </option>
                                              {selectedFlow?.etapas?.filter(e => e.id !== automacao.etapaOrigemId).map(etapa => (
                                                <option key={etapa.id} value={etapa.id}>
                                                  {etapa.nome}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          
                                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <div>
                                              <label className="block text-xs text-gray-600 mb-1">Campo</label>
                                              <select
                                                value={regra.condicao?.campoId || ''}
                                                onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  condicao: { ...regra.condicao, campoId: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                              >
                                                <option value="">Selecione um campo</option>
                                                {getCamposEtapa(regra.condicao?.etapaOrigemCampoId || automacao.etapaOrigemId).map(c => (
                                                    <option key={c.id} value={c.id}>{c.nome}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <div>
                                              <label className="block text-xs font-medium text-gray-700 mb-1">Operador</label>
                                            <select
                                              value={regra.condicao?.operador || 'preenchido'}
                                              onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                ...regra,
                                                condicao: { ...regra.condicao, operador: e.target.value }
                                              })}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                            >
                                              <option value="preenchido">está preenchido</option>
                                              <option value="vazio">está vazio</option>
                                              <option value="igual">é igual a</option>
                                              <option value="diferente">é diferente de</option>
                                              <option value="contem">contém</option>
                                              <option value="nao_contem">não contém</option>
                                              <option value="maior">é maior que</option>
                                              <option value="menor">é menor que</option>
                                              <option value="maior_igual">é igual ou maior que</option>
                                              <option value="dias_restantes">faltam X dias ou menos</option>
                                            </select>
                                            </div>
                                            <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Valor</label>
                                            {(() => {
                                              const campoSel = campos.find(c => c.id === (regra.condicao?.campoId || ''));
                                              const op = regra.condicao?.operador;
                                              const precisaValor = !['preenchido', 'vazio', 'maior_igual'].includes(op || '');
                                              // Campo especial para dias_restantes
                                              if (op === 'dias_restantes') {
                                                return (
                                                  <input
                                                    type="number"
                                                    min="1"
                                                    value={String(regra.condicao?.valor ?? '')}
                                                    onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                      ...regra,
                                                      condicao: { ...regra.condicao, valor: e.target.value }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    placeholder="Ex: 30 (dias)"
                                                  />
                                                );
                                              }
                                              if (!precisaValor) {
                                                return (
                                                  <input
                                                    type="text"
                                                    value=""
                                                    disabled
                                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-400"
                                                    placeholder="Não aplicável para este operador"
                                                  />
                                                );
                                              }
                                              if (campoSel?.opcoes && campoSel.opcoes.length > 0) {
                                                return (
                                                  <select
                                                    value={String(regra.condicao?.valor ?? '')}
                                                    onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                      ...regra,
                                                      condicao: { ...regra.condicao, valor: e.target.value }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                  >
                                                    <option value="">Escolher...</option>
                                                    {campoSel.opcoes.map((opt: string) => (
                                                      <option key={opt} value={opt}>{opt}</option>
                                                    ))}
                                                  </select>
                                                );
                                              }
                                              if (campoSel?.tipo === 'valor') {
                                                return (
                                                  <input
                                                    type="number"
                                                    value={String(regra.condicao?.valor ?? '')}
                                                    onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                      ...regra,
                                                      condicao: { ...regra.condicao, valor: e.target.value }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    placeholder="Digite um número"
                                                  />
                                                );
                                              }
                                              if (campoSel?.tipo === 'data' || campoSel?.tipo === 'data_vencimento') {
                                                return (
                                                  <input
                                                    type={campoSel.tipo === 'data_vencimento' ? 'datetime-local' : 'date'}
                                                    value={String(regra.condicao?.valor ?? '')}
                                                    onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                      ...regra,
                                                      condicao: { ...regra.condicao, valor: e.target.value }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                  />
                                                );
                                              }
                                              return (
                                                <input
                                                  type="text"
                                                  value={String(regra.condicao?.valor ?? '')}
                                                  onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                    ...regra,
                                                    condicao: { ...regra.condicao, valor: e.target.value }
                                                  })}
                                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                  placeholder="Digite o valor"
                                                />
                                              );
                                            })()}
                                            </div>
                                          </div>
                                        </div>

                                        {/* ENTÃO */}
                                        <div className="mt-3 p-3 rounded-lg bg-indigo-50">
                                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                                            <div>
                                              <label className="block text-xs font-medium text-gray-700 mb-1">ENTÃO</label>
                                              <select
                                                value={regra.acaoEntao?.tipo || 'enviar'}
                                                onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  acaoEntao: { ...regra.acaoEntao, tipo: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                              >
                                                <option value="enviar">enviar</option>
                                                <option value="nao_enviar">não enviar</option>
                                              </select>
                                            </div>
                                            {regra.acaoEntao?.tipo === 'enviar' && (
                                              <>
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">Template</label>
                                                  <select
                                                    value={regra.acaoEntao?.templateId || ''}
                                                    onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                      ...regra,
                                                      acaoEntao: { ...regra.acaoEntao, templateId: e.target.value }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                  >
                                                    <option value="">Usar template geral</option>
                                                    {templates.map(t => (
                                                      <option key={t.id} value={t.id}>{t.nome} - {t.assunto}</option>
                                                    ))}
                                                  </select>
                                                </div>
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">Destinatários (opcional)</label>
                                                  <input
                                                    type="text"
                                                    value={(regra.acaoEntao?.destinatarios || []).join(', ')}
                                                    onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                      ...regra,
                                                      acaoEntao: { ...regra.acaoEntao, destinatarios: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    placeholder="email1@empresa.com, email2@empresa.com"
                                                  />
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        </div>

                                        {/* SENÃO */}
                                        <div className="mt-3 p-3 rounded-lg bg-yellow-50">
                                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                                            <div>
                                              <label className="block text-xs font-medium text-gray-700 mb-1">SENÃO</label>
                                              <select
                                                value={regra.acaoSenao?.tipo || 'nao_enviar'}
                                                onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  acaoSenao: { ...regra.acaoSenao, tipo: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                              >
                                                <option value="nao_enviar">não enviar</option>
                                                <option value="enviar">enviar</option>
                                              </select>
                                            </div>
                                            {regra.acaoSenao?.tipo === 'enviar' && (
                                              <>
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">Template</label>
                                                  <select
                                                    value={regra.acaoSenao?.templateId || ''}
                                                    onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                      ...regra,
                                                      acaoSenao: { ...regra.acaoSenao, templateId: e.target.value }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                  >
                                                    <option value="">Usar template geral</option>
                                                    {templates.map(t => (
                                                      <option key={t.id} value={t.id}>{t.nome} - {t.assunto}</option>
                                                    ))}
                                                  </select>
                                                </div>
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">Destinatários (opcional)</label>
                                                  <input
                                                    type="text"
                                                    value={(regra.acaoSenao?.destinatarios || []).join(', ')}
                                                    onChange={(e) => atualizarRegraEmail(acaoIndex, regraIndex, {
                                                      ...regra,
                                                      acaoSenao: { ...regra.acaoSenao, destinatarios: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    placeholder="email1@empresa.com, email2@empresa.com"
                                                  />
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Configuração específica para Enviar WhatsApp */}
                          {acao.tipo === 'enviar_whatsapp' && (
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Template de WhatsApp
                                </label>
                                <select
                                  value={acao.configuracao.templateId || ''}
                                  onChange={(e) => atualizarAcao(acaoIndex, {
                                    ...acao,
                                    configuracao: { ...acao.configuracao, templateId: e.target.value }
                                  })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                  disabled={loadingTemplates}
                                >
                                  <option value="">{loadingTemplates ? 'Carregando templates...' : 'Selecione um template de WhatsApp'}</option>
                                  {whatsappTemplates.map((template) => (
                                    <option key={template.id} value={template.id}>
                                      {template.nome} {template.ativo ? '' : '(Inativo)'}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Destinatários (números de WhatsApp)
                                </label>
                                <input
                                  type="text"
                                  value={(acao.configuracao.destinatarios || []).join(', ')}
                                  onChange={(e) => atualizarAcao(acaoIndex, {
                                    ...acao,
                                    configuracao: { 
                                      ...acao.configuracao, 
                                      destinatarios: e.target.value.split(',').map(s => s.trim()).filter(Boolean) 
                                    }
                                  })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                  placeholder="+5548999999999, +5548888888888"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  Separe múltiplos números com vírgula. Use formato internacional (+55...)
                                </p>
                              </div>

                              <div className="flex items-center gap-2 bg-indigo-50 p-2 rounded border border-indigo-100">
                                <input
                                  type="checkbox"
                                  checked={!!(acao.configuracao as any).enviarSomentePrimeiraVez}
                                  onChange={(e) => atualizarAcao(acaoIndex, {
                                    ...acao,
                                    configuracao: { ...acao.configuracao, enviarSomentePrimeiraVez: e.target.checked }
                                  })}
                                  className="rounded"
                                />
                                <label className="text-xs text-emerald-800">
                                  Enviar apenas na primeira vez que a condição ficar verdadeira
                                </label>
                              </div>

                              {/* Configurações adicionais placeholder */}
                              <div className="bg-indigo-50 p-3 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                  <input type="checkbox" className="rounded" />
                                  <label className="flex items-center gap-2 text-sm">
                                    Incluir mídia do template
                                  </label>
                                </div>
                                <div className="flex items-center gap-2">
                                  <input type="checkbox" className="rounded" />
                                  <label className="flex items-center gap-2 text-sm">
                                    Agendar envio
                                  </label>
                                </div>
                              </div>

                              {/* Regras condicionais de envio WhatsApp (SE/ENTÃO/SENÃO) */}
                              {(!acao.configuracao.regrasWhatsapp || acao.configuracao.regrasWhatsapp.length === 0) ? (
                                <div className="bg-indigo-50 p-3 rounded-lg text-center">
                                  <h4 className="text-sm font-medium text-indigo-800 mb-2">Regras de Envio Condicional</h4>
                                  <p className="text-xs text-indigo-700 mb-3">
                                    Opcional: defina condições para enviar (ou não) este WhatsApp
                                  </p>
                                  <button
                                    onClick={() => adicionarRegraWhatsApp(acaoIndex)}
                                    className="bg-indigo-600 text-white px-4 py-2 rounded text-sm hover:bg-indigo-700 inline-flex items-center gap-2"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    Adicionar Regra de Envio
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-medium text-gray-800">Regras de Envio ({acao.configuracao.regrasWhatsapp.length})</h4>
                                    <button
                                      onClick={() => adicionarRegraWhatsApp(acaoIndex)}
                                      className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700 flex items-center gap-1"
                                    >
                                      <PlusIcon className="h-3 w-3" />
                                      Nova Regra
                                    </button>
                                  </div>

                                  <div className="space-y-3">
                                    {acao.configuracao.regrasWhatsapp.map((regra: any, regraIndex: number) => (
                                      <div key={regraIndex} className="border border-gray-200 p-4 rounded-lg bg-white">
                                        <div className="flex items-center justify-between mb-3">
                                          <h5 className="text-sm font-medium text-gray-800">Regra {regraIndex + 1}</h5>
                                          <button
                                            onClick={() => removerRegraWhatsApp(acaoIndex, regraIndex)}
                                            className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </button>
                                        </div>

                                        {/* Condição */}
                                        <div className="mb-3 p-3 rounded-lg bg-gray-50">
                                          <label className="block text-xs font-medium text-gray-700 mb-2">SE (Condição)</label>
                                          
                                          {/* Seletor de Etapa */}
                                          <div className="mb-3">
                                            <label className="block text-xs text-gray-600 mb-1">
                                              🔀 Buscar campo da etapa:
                                            </label>
                                            <select
                                              value={regra.condicao?.etapaOrigemCampoId || automacao.etapaOrigemId}
                                              onChange={(e) => atualizarRegraWhatsApp(acaoIndex, regraIndex, {
                                                ...regra,
                                                condicao: { ...regra.condicao, etapaOrigemCampoId: e.target.value, campoId: '' }
                                              })}
                                              className="w-full px-3 py-2 border border-blue-300 rounded-lg bg-blue-50 text-sm"
                                            >
                                              <option value={automacao.etapaOrigemId}>
                                                ✓ {selectedFlow?.etapas?.find(e => e.id === automacao.etapaOrigemId)?.nome || 'Etapa atual'}
                                              </option>
                                              {selectedFlow?.etapas?.filter(e => e.id !== automacao.etapaOrigemId).map(etapa => (
                                                <option key={etapa.id} value={etapa.id}>
                                                  {etapa.nome}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          
                                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <div>
                                              <label className="block text-xs text-gray-600 mb-1">Campo</label>
                                              <select
                                                value={regra.condicao?.campoId || ''}
                                                onChange={(e) => atualizarRegraWhatsApp(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  condicao: { ...regra.condicao, campoId: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                              >
                                                <option value="">Selecione o campo</option>
                                                {getCamposEtapa(regra.condicao?.etapaOrigemCampoId || automacao.etapaOrigemId).map(campo => (
                                                    <option key={campo.id} value={campo.id}>{campo.nome}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <div>
                                              <select
                                                value={regra.condicao?.operador || 'preenchido'}
                                                onChange={(e) => atualizarRegraWhatsApp(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  condicao: { ...regra.condicao, operador: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                              >
                                                <option value="preenchido">está preenchido</option>
                                                <option value="vazio">está vazio</option>
                                                <option value="igual">é igual a</option>
                                                <option value="diferente">é diferente de</option>
                                                <option value="contem">contém</option>
                                                <option value="nao_contem">não contém</option>
                                                <option value="maior">é maior que</option>
                                                <option value="menor">é menor que</option>
                                                <option value="maior_igual">é igual ou maior que</option>
                                                <option value="dias_restantes">faltam X dias ou menos</option>
                                              </select>
                                            </div>
                                            <div>
                                              {(() => {
                                                const op = regra.condicao?.operador || 'preenchido';
                                                const needsValue = !['preenchido', 'vazio', 'maior_igual'].includes(op);
                                                if (!needsValue) return <div className="text-xs text-gray-500 pt-2">Não é necessário valor</div>;
                                                
                                                // Campo especial para dias_restantes
                                                if (op === 'dias_restantes') {
                                                  return (
                                                    <input
                                                      type="number"
                                                      min="1"
                                                      value={String(regra.condicao?.valor ?? '')}
                                                      onChange={(e) => atualizarRegraWhatsApp(acaoIndex, regraIndex, {
                                                        ...regra,
                                                        condicao: { ...regra.condicao, valor: e.target.value }
                                                      })}
                                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                      placeholder="Ex: 30 (dias)"
                                                    />
                                                  );
                                                }
                                                if (!needsValue) return <div className="text-xs text-gray-500 pt-2">Não é necessário valor</div>;
                                                
                                                return (
                                                  <input
                                                    type="text"
                                                    value={regra.condicao?.valor || ''}
                                                    onChange={(e) => atualizarRegraWhatsApp(acaoIndex, regraIndex, {
                                                      ...regra,
                                                      condicao: { ...regra.condicao, valor: e.target.value }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    placeholder="Digite o valor"
                                                  />
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        </div>

                                        {/* ENTÃO */}
                                        <div className="mt-3 p-3 rounded-lg bg-indigo-50">
                                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                                            <div>
                                              <label className="block text-xs font-medium text-gray-700 mb-1">ENTÃO</label>
                                              <select
                                                value={regra.acaoEntao?.tipo || 'enviar'}
                                                onChange={(e) => atualizarRegraWhatsApp(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  acaoEntao: { ...regra.acaoEntao, tipo: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                              >
                                                <option value="enviar">Enviar WhatsApp</option>
                                                <option value="nao_enviar">Não enviar</option>
                                              </select>
                                            </div>
                                            {regra.acaoEntao?.tipo === 'enviar' && (
                                              <>
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">Template</label>
                                                  <select
                                                    value={regra.acaoEntao?.templateId || acao.configuracao.templateId || ''}
                                                    onChange={(e) => atualizarRegraWhatsApp(acaoIndex, regraIndex, {
                                                      ...regra,
                                                      acaoEntao: { ...regra.acaoEntao, templateId: e.target.value }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                  >
                                                    <option value="">Usar template padrão</option>
                                                    {whatsappTemplates.map((template) => (
                                                      <option key={template.id} value={template.id}>
                                                        {template.nome}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </div>
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">Destinatários</label>
                                                  <input
                                                    type="text"
                                                    value={(regra.acaoEntao?.destinatarios || []).join(', ')}
                                                    onChange={(e) => atualizarRegraWhatsApp(acaoIndex, regraIndex, {
                                                      ...regra,
                                                      acaoEntao: { ...regra.acaoEntao, destinatarios: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    placeholder="+5548999999999"
                                                  />
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        </div>

                                        {/* SENÃO */}
                                        <div className="mt-3 p-3 rounded-lg bg-red-50">
                                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                                            <div>
                                              <label className="block text-xs font-medium text-gray-700 mb-1">SENÃO</label>
                                              <select
                                                value={regra.acaoSenao?.tipo || 'nao_enviar'}
                                                onChange={(e) => atualizarRegraWhatsApp(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  acaoSenao: { ...regra.acaoSenao, tipo: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                              >
                                                <option value="nao_enviar">Não enviar</option>
                                                <option value="enviar">Enviar WhatsApp</option>
                                              </select>
                                            </div>
                                            {regra.acaoSenao?.tipo === 'enviar' && (
                                              <>
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">Template</label>
                                                  <select
                                                    value={regra.acaoSenao?.templateId || acao.configuracao.templateId || ''}
                                                    onChange={(e) => atualizarRegraWhatsApp(acaoIndex, regraIndex, {
                                                      ...regra,
                                                      acaoSenao: { ...regra.acaoSenao, templateId: e.target.value }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                  >
                                                    <option value="">Usar template padrão</option>
                                                    {whatsappTemplates.map((template) => (
                                                      <option key={template.id} value={template.id}>
                                                        {template.nome}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </div>
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">Destinatários</label>
                                                  <input
                                                    type="text"
                                                    value={(regra.acaoSenao?.destinatarios || []).join(', ')}
                                                    onChange={(e) => atualizarRegraWhatsApp(acaoIndex, regraIndex, {
                                                      ...regra,
                                                      acaoSenao: { ...regra.acaoSenao, destinatarios: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                    placeholder="+5548999999999"
                                                  />
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Configuração específica para Exibir/Ocultar */}
                          {acao.tipo === 'exibir_ocultar' && (
                            <div className="space-y-3">
                              {/* Interface inicial */}
                              {(!acao.configuracao.regras || acao.configuracao.regras.length === 0) && (
                                <div className="bg-blue-50 p-3 rounded-lg text-center">
                                  <h4 className="text-sm font-medium text-blue-800 mb-2">
                                    Controle de Visibilidade de Campos
                                  </h4>
                                  <p className="text-xs text-blue-700 mb-3">
                                    Configure quando campos devem aparecer ou desaparecer com base nas condições
                                  </p>
                                  <button
                                    onClick={() => adicionarRegraVisibilidade(acaoIndex)}
                                    className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 flex items-center gap-2 mx-auto"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    Adicionar Primeira Regra
                                  </button>
                                </div>
                              )}
                              
                              {/* Lista de regras */}
                              {acao.configuracao.regras && acao.configuracao.regras.length > 0 && (
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-medium text-gray-800">
                    Regras de Visibilidade ({acao.configuracao.regras.length})
                                    </h4>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => minimizarTodasRegras(acaoIndex, acao.configuracao.regras.length)}
                                        className="text-gray-500 px-3 py-1 rounded text-sm hover:bg-gray-100 flex items-center gap-1 border border-gray-300"
                                      >
                                        {Array.from({ length: acao.configuracao.regras.length }, (_, i) => `${acaoIndex}-${i}`).every(k => regrasMinimizadas.has(k))
                                          ? '▼ Expandir todas'
                                          : '▲ Minimizar todas'}
                                      </button>
                                      <button
                                        onClick={() => adicionarRegraVisibilidade(acaoIndex)}
                                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 flex items-center gap-1"
                                      >
                                        <PlusIcon className="h-3 w-3" />
                                        Nova Regra
                                      </button>
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {acao.configuracao.regras.map((regra, regraIndex) => (
                                      <div key={regraIndex} className="border border-gray-200 rounded-lg bg-white">
                                        {/* Header da Regra - sempre visível, clicável para toggle */}
                                        <div 
                                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 rounded-t-lg"
                                          onClick={() => toggleRegraMinimizada(acaoIndex, regraIndex)}
                                        >
                                            <h5 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                              <span className="text-gray-400 text-xs">{regrasMinimizadas.has(`${acaoIndex}-${regraIndex}`) ? '▶' : '▼'}</span>
                                              Regra {regraIndex + 1}
                                              {regra.temEntao && <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">Com ENTÃO</span>}
                                              {regra.temSenao && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">Com SENÃO</span>}
                                            </h5>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); removerRegraVisibilidade(acaoIndex, regraIndex); }}
                                              className="text-red-500 text-xs hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1"
                                            >
                                              <TrashIcon className="h-3 w-3" /> Remover regra
                                            </button>
                                        </div>

                                        {/* Resumo quando minimizada */}
                                        {regrasMinimizadas.has(`${acaoIndex}-${regraIndex}`) && (
                                          <div className="px-3 pb-2 text-xs text-gray-500">
                                            {(() => {
                                              const ids = regra.condicao?.campoIds || (regra.condicao?.campoId ? [regra.condicao.campoId] : []);
                                              const nomeCampo = ids.map((id: string) => campos.find(c => c.id === id)?.nome).filter(Boolean).join(', ') || '...';
                                              const elementosNomes = regra.elementosAfetados?.map((el: any) => el.nome).filter(Boolean).join(', ') || '...';
                                              return (
                                                <span>
                                                  SE <strong>{nomeCampo}</strong> {regra.condicao?.operador || '...'} {regra.condicao?.valor ? `"${regra.condicao.valor}"` : ''} → <strong>{regra.acao || 'mostrar'}</strong> {elementosNomes}
                                                </span>
                                              );
                                            })()}
                                          </div>
                                        )}

                                        {/* Conteúdo expandido da regra */}
                                        {!regrasMinimizadas.has(`${acaoIndex}-${regraIndex}`) && (
                                        <div className="p-3 pt-0 space-y-3">
                                          <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                                            <h6 className="text-sm font-medium text-blue-800 mb-3 flex items-center gap-2">
                                              SE (Condição Principal)
                                            </h6>
                                            
                                            {/* Seletor de Etapa */}
                                            <div className="mb-3">
                                              <label className="block text-xs text-gray-700 mb-1">
                                                Buscar campo da etapa:
                                              </label>
                                              <select
                                                value={regra.condicao?.etapaOrigemCampoId || automacao.etapaOrigemId}
                                                onChange={(e) => {
                                                  const novaRegra = {
                                                    ...regra,
                                                    condicao: { ...regra.condicao, etapaOrigemCampoId: e.target.value, campoId: '' }
                                                  };
                                                  atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                }}
                                                className="w-full px-3 py-2 border border-blue-300 rounded-lg bg-white text-sm"
                                              >
                                                <option value={automacao.etapaOrigemId}>
                                                  {selectedFlow?.etapas?.find(e => e.id === automacao.etapaOrigemId)?.nome || 'Etapa atual'} (atual)
                                                </option>
                                                {selectedFlow?.etapas?.filter(e => e.id !== automacao.etapaOrigemId).map(etapa => (
                                                  <option key={etapa.id} value={etapa.id}>
                                                    {etapa.nome}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                            
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className="text-sm text-gray-700">Se o(s) campo(s)</span>
                                              <div className="relative" style={{ minWidth: '250px' }}>
                                                <div
                                                  onClick={(e) => {
                                                    const trigger = e.currentTarget;
                                                    const el = (trigger.nextElementSibling as HTMLElement);
                                                    if (el.style.display === 'none') {
                                                      el.style.display = 'block';
                                                      const rect = trigger.getBoundingClientRect();
                                                      const spaceBelow = window.innerHeight - rect.bottom;
                                                      el.style.left = rect.left + 'px';
                                                      el.style.width = Math.max(400, rect.width) + 'px';
                                                      if (spaceBelow < 280) {
                                                        el.style.bottom = (window.innerHeight - rect.top + 4) + 'px';
                                                        el.style.top = 'auto';
                                                      } else {
                                                        el.style.top = (rect.bottom + 4) + 'px';
                                                        el.style.bottom = 'auto';
                                                      }
                                                      const closeOnOutsideClick = (evt: Event) => {
                                                        if (!el.contains(evt.target as Node) && !trigger.contains(evt.target as Node)) {
                                                          el.style.display = 'none';
                                                          document.removeEventListener('mousedown', closeOnOutsideClick);
                                                        }
                                                      };
                                                      setTimeout(() => document.addEventListener('mousedown', closeOnOutsideClick), 0);
                                                    } else {
                                                      el.style.display = 'none';
                                                    }
                                                  }}
                                                  className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white cursor-pointer flex items-center justify-between gap-2"
                                                >
                                                  <span className="truncate text-gray-700">
                                                    {(() => {
                                                      const ids = regra.condicao?.campoIds || (regra.condicao?.campoId ? [regra.condicao.campoId] : []);
                                                      const etapaSel = selectedFlow?.etapas?.find(
                                                        e => e.id === (regra.condicao?.etapaOrigemCampoId || automacao.etapaOrigemId)
                                                      );
                                                      const nomes = ids.map((id: string) => getCamposEtapa(etapaSel?.id || automacao.etapaOrigemId).find((c: any) => c.id === id)?.nome).filter(Boolean);
                                                      return nomes.length > 0 ? nomes.join(', ') : 'Escolher campos...';
                                                    })()}
                                                  </span>
                                                  <span className="text-gray-400 text-xs">▼</span>
                                                </div>
                                                <div style={{ display: 'none' }} className="fixed z-[9999] bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                                                  {(() => {
                                                    const etapaSel = selectedFlow?.etapas?.find(
                                                      e => e.id === (regra.condicao?.etapaOrigemCampoId || automacao.etapaOrigemId)
                                                    );
                                                    const camposEtapaSe = getCamposEtapa(etapaSel?.id || automacao.etapaOrigemId);
                                                    const idsAtual = regra.condicao?.campoIds || (regra.condicao?.campoId ? [regra.condicao.campoId] : []);
                                                    return camposEtapaSe.map((campo: any) => {
                                                      const isSelected = idsAtual.includes(campo.id);
                                                      return (
                                                        <label key={campo.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-blue-50 cursor-pointer text-xs">
                                                          <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={(ev) => {
                                                              let novosIds = [...idsAtual];
                                                              if (ev.target.checked) {
                                                                novosIds.push(campo.id);
                                                              } else {
                                                                novosIds = novosIds.filter((id: string) => id !== campo.id);
                                                              }
                                                              const novaRegra = {
                                                                ...regra,
                                                                condicao: { ...regra.condicao, campoId: novosIds[0] || '', campoIds: novosIds }
                                                              };
                                                              atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                            }}
                                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                                                          />
                                                          <span className="text-gray-700">{campo.nome}</span>
                                                        </label>
                                                      );
                                                    });
                                                  })()}
                                                </div>
                                              </div>
                                              <select 
                                                value={regra.condicao?.operador || 'preenchido'}
                                                onChange={(e) => {
                                                  const novaRegra = {
                                                    ...regra,
                                                    condicao: { ...regra.condicao, operador: e.target.value as any }
                                                  };
                                                  atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                }}
                                                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white min-w-[130px]"
                                              >
                                                <option value="preenchido">estiver preenchido</option>
                                                <option value="vazio">estiver vazio</option>
                                                <option value="igual">for igual a</option>
                                                <option value="diferente">for diferente de</option>
                                                <option value="contem">contiver</option>
                                                <option value="maior">for maior que</option>
                                                <option value="menor">for menor que</option>
                                                <option value="maior_igual">for igual ou maior que</option>
                                                <option value="dias_restantes">faltarem X dias ou menos</option>
                                              </select>
                                              {['igual', 'diferente', 'contem', 'maior', 'menor', 'dias_restantes'].includes(regra.condicao?.operador || '') && (

                                                (() => {
                                                  // Para dias_restantes, sempre mostrar input numérico
                                                  if (regra.condicao?.operador === 'dias_restantes') {
                                                    return (
                                                      <input
                                                        type="number"
                                                        min="1"
                                                        value={regra.condicao?.valor || ''}
                                                        onChange={(e) => {
                                                          const novaRegra = {
                                                            ...regra,
                                                            condicao: { ...regra.condicao, valor: e.target.value }
                                                          };
                                                          atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                        }}
                                                        placeholder="Ex: 30 (dias)"
                                                        className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm w-32"
                                                      />
                                                    );
                                                  }

                                                  // Busca o campo selecionado para verificar se tem opções

                                                  const campoSelecionado = campos.find(campo => campo.id === regra.condicao?.campoId);

                                                  const temOpcoes = campoSelecionado?.opcoes && campoSelecionado.opcoes.length > 0;

                                                  

                                                  // Se for "igual" ou "diferente" e o campo tem opções, mostra select

                                                  if (['igual', 'diferente'].includes(regra.condicao?.operador || '') && temOpcoes) {

                                                    return (

                                                      <select

                                                        value={regra.condicao?.valor || ''}

                                                        onChange={(e) => {

                                                          const novaRegra = {

                                                            ...regra,

                                                            condicao: { ...regra.condicao, valor: e.target.value }

                                                          };

                                                          atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);

                                                        }}

                                                        className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[120px]"

                                                      >

                                                        <option value="">Selecione uma opção...</option>

                                                        {campoSelecionado?.opcoes?.map((opcao, opcaoIndex) => (

                                                          <option key={opcaoIndex} value={opcao}>{opcao}</option>

                                                        ))}

                                                      </select>

                                                    );

                                                  }

                                                  

                                                  // Senão, mostra input de texto normal

                                                  return (

                                                    <input

                                                      type="text"

                                                      value={regra.condicao?.valor || ''}

                                                      onChange={(e) => {

                                                        const novaRegra = {

                                                          ...regra,

                                                          condicao: { ...regra.condicao, valor: e.target.value }

                                                        };

                                                        atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);

                                                      }}

                                                      placeholder="Digite o valor..."

                                                      className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[120px]"

                                                    />

                                                  );

                                                })()
                                              )}
                                            </div>

                                            {/* Botão para adicionar condição E (AND) e OU (OR) */}
                                            <div className="mt-3 flex items-center gap-2">
                                              <button
                                                onClick={() => adicionarCondicaoE(acaoIndex, regraIndex, 'E')}
                                                className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600 flex items-center gap-1"
                                              >
                                                <PlusIcon className="h-3 w-3" />
                                                E (AND)
                                              </button>
                                              <button
                                                onClick={() => adicionarCondicaoE(acaoIndex, regraIndex, 'OU')}
                                                className="bg-orange-500 text-white px-3 py-1 rounded text-xs hover:bg-orange-600 flex items-center gap-1"
                                              >
                                                <PlusIcon className="h-3 w-3" />
                                                OU (OR)
                                              </button>
                                              <span className="text-[0.65rem] text-gray-500">Adicionar condição adicional</span>
                                            </div>
                                          </div>

                                          {/* Condições E (AND) e OU (OR) adicionais */}
                                          {regra.condicoesAdicionais && regra.condicoesAdicionais.length > 0 && (
                                            <div className="space-y-2">
                                              {regra.condicoesAdicionais.map((condE: any, condIndex: number) => {
                                                const isOU = condE.tipoLogico === 'OU';
                                                return (
                                                <div key={condIndex} className={isOU ? "bg-orange-50 p-3 rounded-lg border-l-4 border-orange-400" : "bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400"}>
                                                  <div className="flex items-center justify-between mb-2">
                                                    <h6 className={`text-xs font-medium flex items-center gap-1 ${isOU ? 'text-orange-700' : 'text-blue-700'}`}>
                                                      {isOU ? 'OU' : 'E'} (Condição {condIndex + 2})
                                                    </h6>
                                                    <button
                                                      onClick={() => removerCondicaoE(acaoIndex, regraIndex, condIndex)}
                                                      className="text-red-500 text-xs hover:text-red-700 hover:bg-red-50 px-1.5 py-0.5 rounded flex items-center gap-1"
                                                    >
                                                      <TrashIcon className="h-3 w-3" /> Remover
                                                    </button>
                                                  </div>
                                                  <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm text-gray-700">{isOU ? 'OU o campo' : 'E o campo'}</span>
                                                    <select
                                                      value={condE.campoId || ''}
                                                      onChange={(e) => {
                                                        atualizarCondicaoE(acaoIndex, regraIndex, condIndex, { ...condE, campoId: e.target.value });
                                                      }}
                                                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white min-w-[140px]"
                                                    >
                                                      <option value="">Escolher campo...</option>
                                                      {getCamposEtapa(regra.condicao?.etapaOrigemCampoId || automacao.etapaOrigemId).map(campo => (
                                                          <option key={campo.id} value={campo.id}>{campo.nome}</option>
                                                        )
                                                      )}
                                                    </select>
                                                    <select
                                                      value={condE.operador || 'preenchido'}
                                                      onChange={(e) => {
                                                        atualizarCondicaoE(acaoIndex, regraIndex, condIndex, { ...condE, operador: e.target.value });
                                                      }}
                                                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white min-w-[130px]"
                                                    >
                                                      <option value="preenchido">estiver preenchido</option>
                                                      <option value="vazio">estiver vazio</option>
                                                      <option value="igual">for igual a</option>
                                                      <option value="diferente">for diferente de</option>
                                                      <option value="contem">contiver</option>
                                                      <option value="maior">for maior que</option>
                                                      <option value="menor">for menor que</option>
                                                      <option value="maior_igual">for igual ou maior que</option>
                                                      <option value="dias_restantes">faltarem X dias ou menos</option>
                                                    </select>
                                                    {['igual', 'diferente', 'contem', 'maior', 'menor', 'dias_restantes'].includes(condE.operador || '') && (
                                                      (() => {
                                                        if (condE.operador === 'dias_restantes') {
                                                          return (
                                                            <input
                                                              type="number"
                                                              min="1"
                                                              value={condE.valor || ''}
                                                              onChange={(e) => {
                                                                atualizarCondicaoE(acaoIndex, regraIndex, condIndex, { ...condE, valor: e.target.value });
                                                              }}
                                                              placeholder="Ex: 30 (dias)"
                                                              className="px-2 py-1 border border-gray-300 rounded text-sm w-32"
                                                            />
                                                          );
                                                        }
                                                        const campoSel = campos.find(c => c.id === condE.campoId);
                                                        const temOpc = campoSel?.opcoes && campoSel.opcoes.length > 0;
                                                        if (['igual', 'diferente'].includes(condE.operador || '') && temOpc) {
                                                          return (
                                                            <select
                                                              value={condE.valor || ''}
                                                              onChange={(e) => {
                                                                atualizarCondicaoE(acaoIndex, regraIndex, condIndex, { ...condE, valor: e.target.value });
                                                              }}
                                                              className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[120px]"
                                                            >
                                                              <option value="">Selecione...</option>
                                                              {campoSel?.opcoes?.map((opcao: string, oi: number) => (
                                                                <option key={oi} value={opcao}>{opcao}</option>
                                                              ))}
                                                            </select>
                                                          );
                                                        }
                                                        return (
                                                          <input
                                                            type="text"
                                                            value={condE.valor || ''}
                                                            onChange={(e) => {
                                                              atualizarCondicaoE(acaoIndex, regraIndex, condIndex, { ...condE, valor: e.target.value });
                                                            }}
                                                            placeholder="Digite o valor..."
                                                            className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[120px]"
                                                          />
                                                        );
                                                      })()
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                              })}
                                            </div>
                                          )}
  
                                          {/* Botão para adicionar/remover ENTÃO */}
                                          <div className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded">
                                            <div className="flex items-center gap-1">
                                              {!regra.temEntao ? (
                                                <button
                                                  onClick={() => {
                                                    const novaRegra = {
                                                      ...regra,
                                                      temEntao: true,
                                                      acao: 'esconder' as const,
                                                      elementosAfetados: [{ id: '', tipo: 'campo' as const, nome: '' }]
                                                    };
                                                    atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                  }}
                                                  className="bg-indigo-500 text-white px-2 py-0.5 rounded text-[0.65rem] hover:bg-indigo-600"
                                                >
                                                  + ENTÃO
                                                </button>
                                              ) : (
                                                <button
                                                  onClick={() => {
                                                    const novaRegra = {
                                                      ...regra,
                                                      temEntao: false,
                                                      acao: undefined,
                                                      elementosAfetados: undefined
                                                    };
                                                    atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                  }}
                                                  className="bg-red-500 text-white px-2 py-0.5 rounded text-[0.65rem] hover:bg-red-600"
                                                >
                                                  Remover ENTÃO
                                                </button>
                                              )}
                                            </div>
                                            <span className="text-[0.6rem] text-gray-500">
                                              {regra.temEntao ? 'Com ação ENTÃO' : 'Sem ação ENTÃO'}
                                            </span>
                                          </div>

                                          {/* Linha 2: Ação ENTÃO (condicional) */}
                                          {regra.temEntao && (
                                            <div className="bg-indigo-50 p-2 rounded-lg border-l-4 border-indigo-500">
                                              <h6 className="text-xs font-medium text-indigo-800 mb-1.5">
                                                ENTÃO (Ação Principal)
                                              </h6>
                                              <div className="flex items-center gap-1.5 flex-wrap">
                                                <select 
                                                  value={regra.acao || 'esconder'}
                                                  onChange={(e) => {
                                                    const novaRegra = {
                                                      ...regra,
                                                      acao: e.target.value as any
                                                    };
                                                    atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                  }}
                                                  className="px-2 py-1 text-xs border border-gray-300 rounded-md font-medium bg-white min-w-[80px]"
                                                >
                                                  <option value="esconder">Esconder</option>
                                                  <option value="mostrar">Mostrar</option>
                                                  <option value="obrigatorio">Tornar Obrigatório</option>
                                                  <option value="opcional">Tornar Opcional</option>
                                                  <option value="desabilitar">Desabilitar</option>
                                                  <option value="habilitar">Habilitar</option>
                                                </select>
                                                <span className="text-xs text-gray-700">o(s) campo(s)</span>
                                                <div className="relative" style={{ minWidth: '200px' }}>
                                                  <div
                                                    onClick={(e) => {
                                                      const trigger = e.currentTarget;
                                                      const el = (trigger.nextElementSibling as HTMLElement);
                                                      if (el.style.display === 'none') {
                                                        el.style.display = 'block';
                                                        const rect = trigger.getBoundingClientRect();
                                                        const spaceBelow = window.innerHeight - rect.bottom;
                                                        el.style.left = rect.left + 'px';
                                                        el.style.width = Math.max(400, rect.width) + 'px';
                                                        if (spaceBelow < 280) {
                                                          el.style.bottom = (window.innerHeight - rect.top + 4) + 'px';
                                                          el.style.top = 'auto';
                                                        } else {
                                                          el.style.top = (rect.bottom + 4) + 'px';
                                                          el.style.bottom = 'auto';
                                                        }
                                                        const closeOnOutsideClick = (evt: Event) => {
                                                          if (!el.contains(evt.target as Node) && !trigger.contains(evt.target as Node)) {
                                                            el.style.display = 'none';
                                                            document.removeEventListener('mousedown', closeOnOutsideClick);
                                                          }
                                                        };
                                                        setTimeout(() => document.addEventListener('mousedown', closeOnOutsideClick), 0);
                                                      } else {
                                                        el.style.display = 'none';
                                                      }
                                                    }}
                                                    className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white cursor-pointer flex items-center justify-between gap-2"
                                                  >
                                                    <span className="truncate text-gray-700">
                                                      {regra.elementosAfetados && regra.elementosAfetados.length > 0
                                                        ? regra.elementosAfetados.map((el: any) => el.nome).join(', ')
                                                        : 'Escolher campos...'}
                                                    </span>
                                                    <span className="text-gray-400 text-xs">▼</span>
                                                  </div>
                                                  <div style={{ display: 'none' }} className="fixed z-[9999] bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                                                    {campos.map(campo => {
                                                      const isSelected = regra.elementosAfetados?.some((el: any) => el.id === campo.id) || false;
                                                      return (
                                                        <label key={campo.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-indigo-50 cursor-pointer text-xs">
                                                          <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                              let novosElementos = [...(regra.elementosAfetados || [])];
                                                              if (e.target.checked) {
                                                                novosElementos.push({ id: campo.id, tipo: 'campo' as const, nome: campo.nomeComEtapa || campo.nome });
                                                              } else {
                                                                novosElementos = novosElementos.filter((el: any) => el.id !== campo.id);
                                                              }
                                                              const novaRegra = { ...regra, elementosAfetados: novosElementos };
                                                              atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                            }}
                                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                                                          />
                                                          <span className="text-gray-700">{campo.nomeComEtapa || campo.nome}</span>
                                                        </label>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                                
                                                {/* Se o campo tem opções (select/checkbox múltiplo), mostrar seleção de opções específicas */}
                                                {(() => {
                                                  // Mostrar opções específicas para cada campo selecionado que tenha opções
                                                  const elementosComOpcoes = (regra.elementosAfetados || []).filter((el: any) => {
                                                    const campo = campos.find(c => c.id === el.id);
                                                    return campo?.opcoes && campo.opcoes.length > 0;
                                                  });
                                                  if (elementosComOpcoes.length === 0) return null;
                                                  return elementosComOpcoes.map((elemento: any, elIdx: number) => {
                                                    const campoAfetado = campos.find(c => c.id === elemento.id);
                                                    if (!campoAfetado?.opcoes) return null;
                                                    const opcoesAtuaisThen = elemento.opcoesEspecificas || [];
                                                    return (
                                                      <div key={elemento.id} className="w-full mt-1.5 p-2 bg-white border border-gray-300 rounded-md">
                                                        <label className="block text-[0.65rem] font-medium text-gray-700 mb-1">
                                                          Opções de "{campoAfetado.nomeComEtapa || campoAfetado.nome}" (deixe vazio para todas):
                                                        </label>
                                                        <div className="space-y-0.5 max-h-24 overflow-y-auto">
                                                          {campoAfetado.opcoes.map((opcao: string, idx: number) => {
                                                            const isChecked = opcoesAtuaisThen.includes(opcao);
                                                            return (
                                                              <label key={idx} className="flex items-center gap-1.5 text-xs hover:bg-gray-50 p-0.5 rounded cursor-pointer">
                                                                <input
                                                                  type="checkbox"
                                                                  checked={isChecked}
                                                                  onChange={(e) => {
                                                                    const novasOpcoes = e.target.checked
                                                                      ? [...opcoesAtuaisThen, opcao]
                                                                      : opcoesAtuaisThen.filter((o: string) => o !== opcao);
                                                                    const novosElementos = regra.elementosAfetados.map((el: any) =>
                                                                      el.id === elemento.id
                                                                        ? { ...el, opcoesEspecificas: novasOpcoes.length > 0 ? novasOpcoes : undefined }
                                                                        : el
                                                                    );
                                                                    const novaRegra = { ...regra, elementosAfetados: novosElementos };
                                                                    atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                                  }}
                                                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3 w-3"
                                                                />
                                                                <span className="text-gray-700">{opcao}</span>
                                                              </label>
                                                            );
                                                          })}
                                                        </div>
                                                      </div>
                                                    );
                                                  });
                                                })()}
                                              </div>

                                              {/* Botão para adicionar mais ações ENTÃO */}
                                              <div className="mt-2">
                                                <button
                                                  onClick={() => adicionarEntaoAdicional(acaoIndex, regraIndex)}
                                                  className="bg-indigo-500 text-white px-3 py-1 rounded text-xs hover:bg-indigo-600 flex items-center gap-1"
                                                >
                                                  <PlusIcon className="h-3 w-3" />
                                                  ENTÃO (adicional)
                                                </button>
                                              </div>
                                            </div>
                                          )}

                                          {/* Ações ENTÃO adicionais */}
                                          {regra.temEntao && regra.acoesEntaoAdicionais && regra.acoesEntaoAdicionais.length > 0 && (
                                            <div className="space-y-2">
                                              {regra.acoesEntaoAdicionais.map((extraEntao: any, ei: number) => (
                                                <div key={ei} className="bg-indigo-50 p-2 rounded-lg border-l-4 border-indigo-400">
                                                  <div className="flex items-center justify-between mb-1.5">
                                                    <h6 className="text-xs font-medium text-indigo-700">
                                                      ENTÃO (Ação {ei + 2})
                                                    </h6>
                                                    <button
                                                      onClick={() => removerEntaoAdicional(acaoIndex, regraIndex, ei)}
                                                      className="text-red-500 text-xs hover:text-red-700 hover:bg-red-50 px-1.5 py-0.5 rounded flex items-center gap-1"
                                                    >
                                                      <TrashIcon className="h-3 w-3" /> Remover
                                                    </button>
                                                  </div>
                                                  <div className="flex items-center gap-1.5 flex-wrap">
                                                    <select
                                                      value={extraEntao.acao || 'esconder'}
                                                      onChange={(e) => atualizarEntaoAdicional(acaoIndex, regraIndex, ei, { acao: e.target.value })}
                                                      className="px-2 py-1 text-xs border border-gray-300 rounded-md font-medium bg-white min-w-[80px]"
                                                    >
                                                      <option value="esconder">Esconder</option>
                                                      <option value="mostrar">Mostrar</option>
                                                      <option value="obrigatorio">Tornar Obrigatório</option>
                                                      <option value="opcional">Tornar Opcional</option>
                                                      <option value="desabilitar">Desabilitar</option>
                                                      <option value="habilitar">Habilitar</option>
                                                    </select>
                                                    <span className="text-xs text-gray-700">o campo</span>
                                                    <select
                                                      value={extraEntao.elemento?.id || ''}
                                                      onChange={(e) => {
                                                        const campoSel = campos.find(c => c.id === e.target.value);
                                                        atualizarEntaoAdicional(acaoIndex, regraIndex, ei, {
                                                          elemento: { id: e.target.value, tipo: 'campo' as const, nome: campoSel?.nome || '' }
                                                        });
                                                      }}
                                                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white min-w-[140px]"
                                                    >
                                                      <option value="">Escolher campo...</option>
                                                      {campos.map(campo => (
                                                        <option key={campo.id} value={campo.id}>{campo.nomeComEtapa || campo.nome}</option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}

                                          {/* Botão para adicionar/remover SENÃO */}
                                          <div className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded">
                                            <div className="flex items-center gap-1">
                                              {!regra.temSenao ? (
                                                <button
                                                  onClick={() => {
                                                    const novaRegra = {
                                                      ...regra,
                                                      temSenao: true,
                                                      acaoSenao: 'mostrar' as const,
                                                      elementosAfetadosSenao: [{ id: '', tipo: 'campo' as const, nome: '' }]
                                                    };
                                                    atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                  }}
                                                  className="bg-orange-500 text-white px-2 py-0.5 rounded text-[0.65rem] hover:bg-orange-600"
                                                >
                                                  + SENÃO
                                                </button>
                                              ) : (
                                                <button
                                                  onClick={() => {
                                                    const novaRegra = {
                                                      ...regra,
                                                      temSenao: false,
                                                      acaoSenao: undefined,
                                                      elementosAfetadosSenao: undefined
                                                    };
                                                    atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                  }}
                                                  className="bg-red-500 text-white px-2 py-0.5 rounded text-[0.65rem] hover:bg-red-600"
                                                >
                                                  Remover SENÃO
                                                </button>
                                              )}
                                            </div>
                                            <span className="text-[0.6rem] text-gray-500">
                                              {regra.temSenao ? 'SE-ENTÃO-SENÃO' : 'SE-ENTÃO'}
                                            </span>
                                          </div>

                                          {/* Linha 3: Ação SENÃO (condicional) */}
                                          {regra.temSenao && (
                                            <div className="bg-orange-50 p-2 rounded-lg border-l-4 border-orange-500">
                                              <h6 className="text-xs font-medium text-orange-800 mb-1.5">
                                                SENÃO (Ação Alternativa)
                                              </h6>
                                              <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-xs text-gray-700">Caso contrário,</span>
                                                <select 
                                                  value={regra.acaoSenao || 'mostrar'}
                                                  onChange={(e) => {
                                                    const novaRegra = {
                                                      ...regra,
                                                      acaoSenao: e.target.value as 'mostrar' | 'esconder'
                                                    };
                                                    atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                  }}
                                                  className="px-2 py-1 text-xs border border-gray-300 rounded-md font-medium bg-white min-w-[80px]"
                                                >
                                                  <option value="esconder">Esconder</option>
                                                  <option value="mostrar">Mostrar</option>
                                                </select>
                                                <span className="text-sm text-gray-700">o(s) campo(s)</span>
                                                <div className="relative" style={{ minWidth: '200px' }}>
                                                  <div
                                                    onClick={(e) => {
                                                      const trigger = e.currentTarget;
                                                      const el = (trigger.nextElementSibling as HTMLElement);
                                                      if (el.style.display === 'none') {
                                                        el.style.display = 'block';
                                                        const rect = trigger.getBoundingClientRect();
                                                        const spaceBelow = window.innerHeight - rect.bottom;
                                                        el.style.left = rect.left + 'px';
                                                        el.style.width = Math.max(400, rect.width) + 'px';
                                                        if (spaceBelow < 280) {
                                                          el.style.bottom = (window.innerHeight - rect.top + 4) + 'px';
                                                          el.style.top = 'auto';
                                                        } else {
                                                          el.style.top = (rect.bottom + 4) + 'px';
                                                          el.style.bottom = 'auto';
                                                        }
                                                        const closeOnOutsideClick = (evt: Event) => {
                                                          if (!el.contains(evt.target as Node) && !trigger.contains(evt.target as Node)) {
                                                            el.style.display = 'none';
                                                            document.removeEventListener('mousedown', closeOnOutsideClick);
                                                          }
                                                        };
                                                        setTimeout(() => document.addEventListener('mousedown', closeOnOutsideClick), 0);
                                                      } else {
                                                        el.style.display = 'none';
                                                      }
                                                    }}
                                                    className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white cursor-pointer flex items-center justify-between gap-2"
                                                  >
                                                    <span className="truncate text-gray-700">
                                                      {regra.elementosAfetadosSenao && regra.elementosAfetadosSenao.length > 0 && regra.elementosAfetadosSenao[0].id
                                                        ? regra.elementosAfetadosSenao.map((el: any) => el.nome).join(', ')
                                                        : 'Escolher campos...'}
                                                    </span>
                                                    <span className="text-gray-400 text-xs">▼</span>
                                                  </div>
                                                  <div style={{ display: 'none' }} className="fixed z-[9999] bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                                                    {campos.map(campo => {
                                                      const isSelected = regra.elementosAfetadosSenao?.some((el: any) => el.id === campo.id) || false;
                                                      return (
                                                        <label key={campo.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-orange-50 cursor-pointer text-xs">
                                                          <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                              let novosElementos = [...(regra.elementosAfetadosSenao || [])].filter((el: any) => el.id);
                                                              if (e.target.checked) {
                                                                novosElementos.push({ id: campo.id, tipo: 'campo' as const, nome: campo.nomeComEtapa || campo.nome });
                                                              } else {
                                                                novosElementos = novosElementos.filter((el: any) => el.id !== campo.id);
                                                              }
                                                              const novaRegra = { ...regra, elementosAfetadosSenao: novosElementos };
                                                              atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                            }}
                                                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 h-3.5 w-3.5"
                                                          />
                                                          <span className="text-gray-700">{campo.nomeComEtapa || campo.nome}</span>
                                                        </label>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                                
                                                {/* Se campos SENÃO têm opções, mostrar seleção de opções específicas */}
                                                {(() => {
                                                  const elementosComOpcoes = (regra.elementosAfetadosSenao || []).filter((el: any) => {
                                                    const campo = campos.find(c => c.id === el.id);
                                                    return campo?.opcoes && campo.opcoes.length > 0;
                                                  });
                                                  if (elementosComOpcoes.length === 0) return null;
                                                  return elementosComOpcoes.map((elemento: any) => {
                                                    const campoAfetado = campos.find(c => c.id === elemento.id);
                                                    if (!campoAfetado?.opcoes) return null;
                                                    const opcoesAtuaisElse = elemento.opcoesEspecificas || [];
                                                    return (
                                                      <div key={elemento.id} className="w-full mt-1.5 p-2 bg-white border border-gray-300 rounded-md">
                                                        <label className="block text-[0.65rem] font-medium text-gray-700 mb-1">
                                                          Opções de "{campoAfetado.nomeComEtapa || campoAfetado.nome}" (deixe vazio para todas):
                                                        </label>
                                                        <div className="space-y-0.5 max-h-24 overflow-y-auto">
                                                          {campoAfetado.opcoes.map((opcao: string, idx: number) => {
                                                            const isChecked = opcoesAtuaisElse.includes(opcao);
                                                            return (
                                                              <label key={idx} className="flex items-center gap-1.5 text-xs hover:bg-gray-50 p-0.5 rounded cursor-pointer">
                                                                <input
                                                                  type="checkbox"
                                                                  checked={isChecked}
                                                                  onChange={(e) => {
                                                                    const novasOpcoes = e.target.checked
                                                                      ? [...opcoesAtuaisElse, opcao]
                                                                      : opcoesAtuaisElse.filter((o: string) => o !== opcao);
                                                                    const novosElementos = regra.elementosAfetadosSenao.map((el: any) =>
                                                                      el.id === elemento.id
                                                                        ? { ...el, opcoesEspecificas: novasOpcoes.length > 0 ? novasOpcoes : undefined }
                                                                        : el
                                                                    );
                                                                    const novaRegra = { ...regra, elementosAfetadosSenao: novosElementos };
                                                                    atualizarRegraVisibilidade(acaoIndex, regraIndex, novaRegra);
                                                                  }}
                                                                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 h-3 w-3"
                                                                />
                                                                <span className="text-gray-700">{opcao}</span>
                                                              </label>
                                                            );
                                                          })}
                                                        </div>
                                                      </div>
                                                    );
                                                  });
                                                })()}
                                              </div>

                                              {/* Botão para adicionar mais ações SENÃO */}
                                              <div className="mt-2">
                                                <button
                                                  onClick={() => adicionarSenaoAdicional(acaoIndex, regraIndex)}
                                                  className="bg-orange-500 text-white px-3 py-1 rounded text-xs hover:bg-orange-600 flex items-center gap-1"
                                                >
                                                  <PlusIcon className="h-3 w-3" />
                                                  SENÃO (adicional)
                                                </button>
                                              </div>
                                            </div>
                                          )}

                                          {/* Ações SENÃO adicionais */}
                                          {regra.temSenao && regra.acoesSenaoAdicionais && regra.acoesSenaoAdicionais.length > 0 && (
                                            <div className="space-y-2">
                                              {regra.acoesSenaoAdicionais.map((extraSenao: any, si: number) => (
                                                <div key={si} className="bg-orange-50 p-2 rounded-lg border-l-4 border-orange-400">
                                                  <div className="flex items-center justify-between mb-1.5">
                                                    <h6 className="text-xs font-medium text-orange-700">
                                                      SENÃO (Ação {si + 2})
                                                    </h6>
                                                    <button
                                                      onClick={() => removerSenaoAdicional(acaoIndex, regraIndex, si)}
                                                      className="text-red-500 text-xs hover:text-red-700 hover:bg-red-50 px-1.5 py-0.5 rounded flex items-center gap-1"
                                                    >
                                                      <TrashIcon className="h-3 w-3" /> Remover
                                                    </button>
                                                  </div>
                                                  <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className="text-xs text-gray-700">Caso contrário,</span>
                                                    <select
                                                      value={extraSenao.acao || 'mostrar'}
                                                      onChange={(e) => atualizarSenaoAdicional(acaoIndex, regraIndex, si, { acao: e.target.value })}
                                                      className="px-2 py-1 text-xs border border-gray-300 rounded-md font-medium bg-white min-w-[80px]"
                                                    >
                                                      <option value="esconder">Esconder</option>
                                                      <option value="mostrar">Mostrar</option>
                                                    </select>
                                                    <span className="text-xs text-gray-700">o campo</span>
                                                    <select
                                                      value={extraSenao.elemento?.id || ''}
                                                      onChange={(e) => {
                                                        const campoSel = campos.find(c => c.id === e.target.value);
                                                        atualizarSenaoAdicional(acaoIndex, regraIndex, si, {
                                                          elemento: { id: e.target.value, tipo: 'campo' as const, nome: campoSel?.nome || '' }
                                                        });
                                                      }}
                                                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white min-w-[140px]"
                                                    >
                                                      <option value="">Escolher campo...</option>
                                                      {campos.map(campo => (
                                                        <option key={campo.id} value={campo.id}>{campo.nomeComEtapa || campo.nome}</option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}

                                          {/* Preview da Regra */}
                                          {regra.condicao?.campoId && regra.elementosAfetados?.length > 0 && regra.elementosAfetados[0]?.id && (
                                            <div className="bg-gray-100 p-3 rounded-lg border">
                                              <p className="text-xs text-gray-600 uppercase tracking-wider font-medium mb-1">Resumo desta regra</p>
                                              <div className="text-sm text-gray-800 space-y-1">
                                                <p>
                                                  <strong>SE</strong> {(regra.condicao?.campoIds?.length || 1) > 1 ? 'os campos' : 'o campo'} "<span className="font-semibold text-blue-600">{
                                                    (() => {
                                                      const ids = regra.condicao?.campoIds || (regra.condicao?.campoId ? [regra.condicao.campoId] : []);
                                                      return ids.map((id: string) => campos.find(c => c.id === id)?.nome).filter(Boolean).join('", "');
                                                    })()
                                                  }</span>"{' '}
                                                  {regra.condicao?.operador === 'preenchido' ? 'estiver preenchido' :
                                                    regra.condicao?.operador === 'vazio' ? 'estiver vazio' :
                                                    regra.condicao?.operador === 'igual' ? `for igual a "${regra.condicao?.valor}"` :
                                                    regra.condicao?.operador === 'diferente' ? `for diferente de "${regra.condicao?.valor}"` :
                                                    regra.condicao?.operador === 'contem' ? `contiver "${regra.condicao?.valor}"` :
                                                    regra.condicao?.operador === 'maior' ? `for maior que "${regra.condicao?.valor}"` :
                                                    regra.condicao?.operador === 'menor' ? `for menor que "${regra.condicao?.valor}"` :
                                                    regra.condicao?.operador === 'maior_igual' ? `for igual ou maior que "${regra.condicao?.valor}"` : 'condição'}
                                                </p>
                                                {regra.condicoesAdicionais && regra.condicoesAdicionais.length > 0 && (
                                                  regra.condicoesAdicionais.map((condE: any, ci: number) => (
                                                    <p key={ci}>
                                                      <strong className={condE.tipoLogico === 'OU' ? "text-orange-600" : "text-blue-600"}>{condE.tipoLogico === 'OU' ? 'OU' : 'E'}</strong> o campo "<span className="font-semibold text-blue-600">{campos.find(c => c.id === condE.campoId)?.nome || '...'}</span>"{' '}
                                                      {condE.operador === 'preenchido' ? 'estiver preenchido' :
                                                        condE.operador === 'vazio' ? 'estiver vazio' :
                                                        condE.operador === 'igual' ? `for igual a "${condE.valor}"` :
                                                        condE.operador === 'diferente' ? `for diferente de "${condE.valor}"` :
                                                        condE.operador === 'contem' ? `contiver "${condE.valor}"` :
                                                        condE.operador === 'maior' ? `for maior que "${condE.valor}"` :
                                                        condE.operador === 'menor' ? `for menor que "${condE.valor}"` :
                                                        condE.operador === 'maior_igual' ? `for igual ou maior que "${condE.valor}"` : 'condição'}
                                                    </p>
                                                  ))
                                                )}
                                                <p>
                                                  <strong>ENTÃO</strong> {
                                                    regra.acao === 'esconder' ? 'esconder' : 
                                                    regra.acao === 'mostrar' ? 'mostrar' :
                                                    regra.acao === 'obrigatorio' ? 'tornar obrigatório' :
                                                    regra.acao === 'opcional' ? 'tornar opcional' :
                                                    regra.acao === 'desabilitar' ? 'desabilitar' :
                                                    regra.acao === 'habilitar' ? 'habilitar' : 'ação'
                                                  } {regra.elementosAfetados?.length > 1 ? 'os campos' : 'o campo'} "<span className="font-semibold text-green-600">{regra.elementosAfetados?.map((el: any) => el.nome).join('", "')}</span>"
                                                </p>
                                                {regra.acoesEntaoAdicionais && regra.acoesEntaoAdicionais.length > 0 && (
                                                  regra.acoesEntaoAdicionais.map((extraE: any, eii: number) => (
                                                    <p key={`entao-${eii}`}>
                                                      <strong className="text-green-600">ENTÃO</strong> {
                                                        extraE.acao === 'esconder' ? 'esconder' :
                                                        extraE.acao === 'mostrar' ? 'mostrar' :
                                                        extraE.acao === 'obrigatorio' ? 'tornar obrigatório' :
                                                        extraE.acao === 'opcional' ? 'tornar opcional' :
                                                        extraE.acao === 'desabilitar' ? 'desabilitar' :
                                                        extraE.acao === 'habilitar' ? 'habilitar' : 'ação'
                                                      } o campo "<span className="font-semibold text-green-600">{extraE.elemento?.nome || '...'}</span>"
                                                    </p>
                                                  ))
                                                )}
                                                {regra.temSenao && regra.elementosAfetadosSenao?.length > 0 && regra.elementosAfetadosSenao[0]?.id && (
                                                  <p>
                                                    <strong>SENÃO</strong> {
                                                      regra.acaoSenao === 'esconder' ? 'esconder' : 
                                                      regra.acaoSenao === 'mostrar' ? 'mostrar' : 'ação'
                                                    } {regra.elementosAfetadosSenao?.length > 1 ? 'os campos' : 'o campo'} "<span className="font-semibold text-orange-600">{regra.elementosAfetadosSenao?.map((el: any) => el.nome).join('", "')}</span>"
                                                  </p>
                                                )}
                                                {regra.acoesSenaoAdicionais && regra.acoesSenaoAdicionais.length > 0 && (
                                                  regra.acoesSenaoAdicionais.map((extraS: any, sii: number) => (
                                                    <p key={`senao-${sii}`}>
                                                      <strong className="text-orange-600">SENÃO</strong> {
                                                        extraS.acao === 'esconder' ? 'esconder' :
                                                        extraS.acao === 'mostrar' ? 'mostrar' : 'ação'
                                                      } o campo "<span className="font-semibold text-orange-600">{extraS.elemento?.nome || '...'}</span>"
                                                    </p>
                                                  ))
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Configuração específica para Atribuir Responsável */}
                          {acao.tipo === 'atribuir_responsavel' && (
                            <div className="space-y-3">
                              {(!acao.configuracao?.regrasResponsavel || !Array.isArray(acao.configuracao?.regrasResponsavel) || acao.configuracao.regrasResponsavel.length === 0) && (
                                <div className="bg-blue-50 p-3 rounded-lg text-center">
                                  <h4 className="text-sm font-medium text-blue-800 mb-2">
                                    Atribuição Automática de Responsável
                                  </h4>
                                  <p className="text-xs text-blue-700 mb-3">
                                    Configure regras para atribuir automaticamente responsáveis aos cards baseado em condições
                                  </p>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      adicionarRegraResponsavel(acaoIndex);
                                    }}
                                    className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 flex items-center gap-2 mx-auto"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    Adicionar Primeira Regra
                                  </button>
                                </div>
                              )}

                              {acao.configuracao?.regrasResponsavel && Array.isArray(acao.configuracao.regrasResponsavel) && acao.configuracao.regrasResponsavel.length > 0 && (
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-medium text-gray-800">
                                      Regras de Atribuição ({acao.configuracao.regrasResponsavel.length})
                                    </h4>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        adicionarRegraResponsavel(acaoIndex);
                                      }}
                                      className="bg-blue-600 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-700 flex items-center gap-1"
                                    >
                                      <PlusIcon className="h-3 w-3" />
                                      Nova Regra
                                    </button>
                                  </div>

                                  {acao.configuracao.regrasResponsavel.map((regra: any, regraIndex: number) => {
                                    // Buscar campos da etapa selecionada (ou etapa de origem por padrão)
                                    const etapaParaCampo = regra.condicao?.etapaOrigemCampoId || automacao.etapaOrigemId;
                                    const etapaCampoSelecionada = selectedFlow?.etapas?.find(e => e.id === etapaParaCampo);
                                    const camposDisponiveis = etapaCampoSelecionada?.campos || [];
                                    const campoSelecionado = camposDisponiveis.find(c => c.id === regra.condicao?.campoId);
                                    
                                    return (
                                      <div key={regraIndex} className="border-2 border-blue-200 rounded-lg p-4 space-y-3 bg-blue-50/50">
                                        <div className="flex items-center justify-between">
                                          <h5 className="text-sm font-semibold text-blue-900">Regra {regraIndex + 1}</h5>
                                          <button
                                            onClick={() => removerRegraResponsavel(acaoIndex, regraIndex)}
                                            className="text-red-600 hover:text-red-800 p-1"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </button>
                                        </div>

                                        {/* Linha 1: Condição (SE) */}
                                        <div className="bg-white p-4 rounded-lg border-l-4 border-blue-500">
                                          <h6 className="text-sm font-medium text-blue-800 mb-3 flex items-center gap-2">
                                            SE (Condição)
                                          </h6>
                                          
                                          {/* Seletor de Etapa */}
                                          <div className="mb-3">
                                            <label className="block text-xs text-gray-600 mb-1">
                                              🔀 Buscar campo da etapa:
                                            </label>
                                            <select
                                              value={regra.condicao?.etapaOrigemCampoId || automacao.etapaOrigemId}
                                              onChange={(e) => {
                                                const novaRegra = {
                                                  ...regra,
                                                  condicao: { ...regra.condicao, etapaOrigemCampoId: e.target.value, campoId: '' }
                                                };
                                                atualizarRegraResponsavel(acaoIndex, regraIndex, novaRegra);
                                              }}
                                              className="w-full px-3 py-2 border border-blue-300 rounded-lg bg-blue-50 text-sm"
                                            >
                                              <option value={automacao.etapaOrigemId}>
                                                ✓ {selectedFlow?.etapas?.find(e => e.id === automacao.etapaOrigemId)?.nome || 'Etapa atual'}
                                              </option>
                                              {selectedFlow?.etapas?.filter(e => e.id !== automacao.etapaOrigemId).map(etapa => (
                                                <option key={etapa.id} value={etapa.id}>
                                                  {etapa.nome}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm text-gray-700">Se o campo</span>
                                            <select
                                              value={regra.condicao?.campoId || ''}
                                              onChange={(e) => {
                                                const novaRegra = {
                                                  ...regra,
                                                  condicao: { ...regra.condicao, campoId: e.target.value }
                                                };
                                                atualizarRegraResponsavel(acaoIndex, regraIndex, novaRegra);
                                              }}
                                              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white min-w-[150px]"
                                            >
                                              <option value="">Selecione um campo...</option>
                                              {Array.isArray(camposDisponiveis) && camposDisponiveis.map(campo => (
                                                <option key={campo.id} value={campo.id}>
                                                  {campo.nome}
                                                </option>
                                              ))}
                                            </select>
                                            <select
                                              value={regra.condicao?.operador || 'igual'}
                                              onChange={(e) => {
                                                const novaRegra = {
                                                  ...regra,
                                                  condicao: { ...regra.condicao, operador: e.target.value }
                                                };
                                                atualizarRegraResponsavel(acaoIndex, regraIndex, novaRegra);
                                              }}
                                              className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[120px]"
                                            >
                                              <option value="igual">for igual a</option>
                                              <option value="diferente">for diferente de</option>
                                              <option value="contem">contiver</option>
                                              <option value="vazio">estiver vazio</option>
                                              <option value="nao_vazio">estiver preenchido</option>
                                              <option value="maior">for maior que</option>
                                              <option value="menor">for menor que</option>
                                            </select>
                                            {regra.condicao?.operador && !['vazio', 'nao_vazio'].includes(regra.condicao.operador) && (
                                              campoSelecionado?.tipo === 'lista' ? (
                                                <select
                                                  value={regra.condicao?.valor || ''}
                                                  onChange={(e) => {
                                                    const novaRegra = {
                                                      ...regra,
                                                      condicao: { ...regra.condicao, valor: e.target.value }
                                                    };
                                                    atualizarRegraResponsavel(acaoIndex, regraIndex, novaRegra);
                                                  }}
                                                  className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[120px]"
                                                >
                                                  <option value="">Selecione...</option>
                                                  {campoSelecionado?.opcoes?.map((opcao, idx) => (
                                                    <option key={idx} value={opcao}>{opcao}</option>
                                                  ))}
                                                </select>
                                              ) : (
                                                <input
                                                  type="text"
                                                  value={regra.condicao?.valor || ''}
                                                  onChange={(e) => {
                                                    const novaRegra = {
                                                      ...regra,
                                                      condicao: { ...regra.condicao, valor: e.target.value }
                                                    };
                                                    atualizarRegraResponsavel(acaoIndex, regraIndex, novaRegra);
                                                  }}
                                                  className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-[120px]"
                                                  placeholder="Valor..."
                                                />
                                              )
                                            )}
                                          </div>
                                        </div>

                                        {/* Linha 2: Ação ENTÃO */}
                                        <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500">
                                          <h6 className="text-sm font-medium text-indigo-800 mb-3 flex items-center gap-2">
                                            ENTÃO (Ação Principal)
                                          </h6>
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm text-gray-700">Atribuir o card para</span>
                                            <select
                                              value={regra.responsavelId || ''}
                                              onChange={(e) => {
                                                const novaRegra = {
                                                  ...regra,
                                                  responsavelId: e.target.value
                                                };
                                                atualizarRegraResponsavel(acaoIndex, regraIndex, novaRegra);
                                              }}
                                              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white min-w-[200px]"
                                              onClick={() => console.log('👥 Colaboradores disponíveis:', colaboradores)}
                                            >
                                              <option value="">Selecione um responsável...</option>
                                              {Array.isArray(colaboradores) && colaboradores.length > 0 ? (
                                                colaboradores.map((colab: any) => (
                                                  <option key={colab.id} value={colab.id}>
                                                    {colab.nome} {colab.cargo ? `- ${colab.cargo}` : ''}
                                                  </option>
                                                ))
                                              ) : (
                                                <option key="loading" disabled>Carregando colaboradores...</option>
                                              )}
                                            </select>
                                          </div>
                                        </div>

                                        {/* Linha 3: Ação SENÃO (Opcional) */}
                                        <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
                                          <h6 className="text-sm font-medium text-orange-800 mb-3 flex items-center gap-2">
                                            SENÃO (Opcional - Ação Alternativa)
                                          </h6>
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm text-gray-700">Caso contrário, atribuir para</span>
                                            <select
                                              value={regra.senao?.responsavelId || ''}
                                              onChange={(e) => {
                                                const novaRegra = {
                                                  ...regra,
                                                  senao: { responsavelId: e.target.value || undefined }
                                                };
                                                atualizarRegraResponsavel(acaoIndex, regraIndex, novaRegra);
                                              }}
                                              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white min-w-[200px]"
                                            >
                                              <option value="">Ninguém (deixar sem responsável)</option>
                                              {Array.isArray(colaboradores) && colaboradores.length > 0 ? (
                                                colaboradores.map((colab: any) => (
                                                  <option key={colab.id} value={colab.id}>
                                                    {colab.nome} {colab.cargo ? `- ${colab.cargo}` : ''}
                                                  </option>
                                                ))
                                              ) : (
                                                <option key="loading" disabled>Carregando colaboradores...</option>
                                              )}
                                            </select>
                                          </div>
                                        </div>

                                        {/* Preview da Regra */}
                                        {regra.condicao?.campoId && regra.responsavelId && (
                                          <div className="bg-gray-100 p-3 rounded-lg border">
                                            <p className="text-xs text-gray-600 uppercase tracking-wider font-medium mb-1">Resumo desta regra</p>
                                            <div className="text-sm text-gray-800 space-y-1">
                                              <p>
                                                <strong>SE</strong> o campo "<span className="font-semibold text-blue-600">{camposDisponiveis?.find(c => c.id === regra.condicao?.campoId)?.nome || ''}</span>"
                                                {regra.condicao?.etapaOrigemCampoId && regra.condicao.etapaOrigemCampoId !== automacao.etapaOrigemId && (
                                                  <span className="text-xs text-gray-500"> (da etapa {etapaCampoSelecionada?.nome})</span>
                                                )}{' '}
                                                {regra.condicao?.operador === 'vazio' ? 'estiver vazio' :
                                                  regra.condicao?.operador === 'nao_vazio' ? 'estiver preenchido' :
                                                  regra.condicao?.operador === 'igual' ? `for igual a "${regra.condicao?.valor}"` :
                                                  regra.condicao?.operador === 'diferente' ? `for diferente de "${regra.condicao?.valor}"` :
                                                  regra.condicao?.operador === 'contem' ? `contiver "${regra.condicao?.valor}"` :
                                                  regra.condicao?.operador === 'maior' ? `for maior que "${regra.condicao?.valor}"` :
                                                  regra.condicao?.operador === 'menor' ? `for menor que "${regra.condicao?.valor}"` : 'condição'}
                                              </p>
                                              <p>
                                                <strong>ENTÃO</strong> atribuir para "<span className="font-semibold text-green-600">{colaboradores?.find((c: any) => c.id === regra.responsavelId)?.nome || ''}</span>"
                                              </p>
                                              {regra.senao?.responsavelId && (
                                                <p>
                                                  <strong>SENÃO</strong> atribuir para "<span className="font-semibold text-orange-600">{colaboradores?.find((c: any) => c.id === regra.senao?.responsavelId)?.nome || ''}</span>"
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                        )}

                                        {/* Campos exclusivos do responsável */}
                                        <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                                          <h6 className="text-sm font-medium text-purple-800 mb-3 flex items-center gap-2">
                                            🔒 Campos exclusivos do responsável (Opcional)
                                          </h6>
                                          <p className="text-xs text-gray-500 mb-2">
                                            Selecione campos que só ficarão visíveis para o responsável atribuído. Outros usuários não verão esses campos.
                                          </p>
                                          <div className="space-y-1">
                                            {Array.isArray(camposDisponiveis) && camposDisponiveis.map((campo: any) => (
                                              <label key={campo.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-purple-100 px-2 py-1 rounded">
                                                <input
                                                  type="checkbox"
                                                  checked={regra.camposExclusivos?.includes(campo.id) || false}
                                                  onChange={(e) => {
                                                    const atuais = regra.camposExclusivos || [];
                                                    const novos = e.target.checked
                                                      ? [...atuais, campo.id]
                                                      : atuais.filter((id: string) => id !== campo.id);
                                                    const novaRegra = {
                                                      ...regra,
                                                      camposExclusivos: novos
                                                    };
                                                    atualizarRegraResponsavel(acaoIndex, regraIndex, novaRegra);
                                                  }}
                                                  className="rounded border-purple-400 text-purple-600 focus:ring-purple-500"
                                                />
                                                <span className="text-gray-700">{campo.nome}</span>
                                              </label>
                                            ))}
                                          </div>
                                          {regra.camposExclusivos?.length > 0 && (
                                            <p className="text-xs text-purple-600 mt-2">
                                              {regra.camposExclusivos.length} campo(s) só visível(is) para o responsável
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Configuração específica para Atribuir API */}
                          {acao.tipo === 'atribuir_api' && (
                            <div className="space-y-3">
                              {(!acao.configuracao?.regrasApi || acao.configuracao.regrasApi.length === 0) && (
                                <div className="bg-orange-50 p-3 rounded-lg text-center">
                                  <h4 className="text-sm font-medium text-orange-800 mb-2">
                                    Atribuir API
                                  </h4>
                                  <p className="text-xs text-orange-700 mb-3">
                                    Configure chamadas de API baseadas em condições de campos
                                  </p>
                                  <button
                                    onClick={() => adicionarRegraApi(acaoIndex)}
                                    className="bg-orange-600 text-white px-4 py-2 rounded text-sm hover:bg-orange-700 flex items-center gap-2 mx-auto"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    Adicionar Primeira Regra
                                  </button>
                                </div>
                              )}

                              {acao.configuracao?.regrasApi && acao.configuracao.regrasApi.length > 0 && (
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-medium text-gray-800">
                                      Regras de API ({acao.configuracao.regrasApi.length})
                                    </h4>
                                    <button
                                      onClick={() => adicionarRegraApi(acaoIndex)}
                                      className="bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700 flex items-center gap-1"
                                    >
                                      <PlusIcon className="h-3 w-3" />
                                      Nova Regra
                                    </button>
                                  </div>

                                  {acao.configuracao.regrasApi.map((regra: any, regraIndex: number) => {
                                    const campoCondicaoSelecionado = campos.find((c: any) => c.id === regra.condicao?.campoId);
                                    const temOpcoesCondicao = campoCondicaoSelecionado?.opcoes && campoCondicaoSelecionado.opcoes.length > 0;
                                    return (
                                    <div key={regraIndex} className="border border-gray-200 rounded-lg bg-white p-3 space-y-3">
                                      <div className="flex items-center justify-between">
                                        <h5 className="text-sm font-semibold text-gray-800">Regra {regraIndex + 1}</h5>
                                        <button
                                          onClick={() => removerRegraApi(acaoIndex, regraIndex)}
                                          className="text-red-500 text-xs hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1"
                                        >
                                          <TrashIcon className="h-3 w-3" /> Remover
                                        </button>
                                      </div>

                                      {/* SE (Condição) */}
                                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                                        <h6 className="text-sm font-medium text-blue-800 mb-2">SE (Condição)</h6>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-sm text-gray-700">Se o campo</span>
                                          <select
                                            value={regra.condicao?.campoId || ''}
                                            onChange={(e) => {
                                              const novaRegra = { ...regra, condicao: { ...regra.condicao, campoId: e.target.value, valor: '' } };
                                              atualizarRegraApi(acaoIndex, regraIndex, novaRegra);
                                            }}
                                            className="px-2 py-1 border border-gray-300 rounded text-sm bg-white"
                                          >
                                            <option value="">Selecionar campo...</option>
                                            {campos.map((campo: any) => (
                                              <option key={campo.id} value={campo.id}>{campo.nome}</option>
                                            ))}
                                          </select>
                                          <select
                                            value={regra.condicao?.operador || 'igual'}
                                            onChange={(e) => {
                                              const novaRegra = { ...regra, condicao: { ...regra.condicao, operador: e.target.value } };
                                              atualizarRegraApi(acaoIndex, regraIndex, novaRegra);
                                            }}
                                            className="px-2 py-1 border border-gray-300 rounded text-sm bg-white"
                                          >
                                            <option value="igual">for igual a</option>
                                            <option value="diferente">for diferente de</option>
                                            <option value="contem">contiver</option>
                                            <option value="preenchido">estiver preenchido</option>
                                            <option value="vazio">estiver vazio</option>
                                          </select>
                                          {regra.condicao?.operador !== 'preenchido' && regra.condicao?.operador !== 'vazio' && (
                                            temOpcoesCondicao ? (
                                              <select
                                                value={regra.condicao?.valor || ''}
                                                onChange={(e) => {
                                                  const novaRegra = { ...regra, condicao: { ...regra.condicao, valor: e.target.value } };
                                                  atualizarRegraApi(acaoIndex, regraIndex, novaRegra);
                                                }}
                                                className="px-2 py-1 border border-gray-300 rounded text-sm bg-white min-w-[120px]"
                                              >
                                                <option value="">Selecione uma opção...</option>
                                                {campoCondicaoSelecionado.opcoes.map((opcao: string, opcaoIndex: number) => (
                                                  <option key={opcaoIndex} value={opcao}>{opcao}</option>
                                                ))}
                                              </select>
                                            ) : (
                                              <input
                                                type="text"
                                                value={regra.condicao?.valor || ''}
                                                onChange={(e) => {
                                                  const novaRegra = { ...regra, condicao: { ...regra.condicao, valor: e.target.value } };
                                                  atualizarRegraApi(acaoIndex, regraIndex, novaRegra);
                                                }}
                                                placeholder="Valor..."
                                                className="px-2 py-1 border border-gray-300 rounded text-sm w-32"
                                              />
                                            )
                                          )}
                                        </div>
                                      </div>

                                      {/* Campo de Parâmetro (compartilhado ENTÃO/SENÃO) */}
                                      <div className="bg-gray-50 p-3 rounded-lg">
                                        <h6 className="text-xs font-medium text-gray-700 mb-2">Campo de Referência (parâmetro para URL)</h6>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <select
                                            value={regra.acaoEntao?.campoParametroId || ''}
                                            onChange={(e) => {
                                              const novaRegra = {
                                                ...regra,
                                                acaoEntao: { ...regra.acaoEntao, campoParametroId: e.target.value },
                                                acaoSenao: regra.acaoSenao ? { ...regra.acaoSenao, campoParametroId: e.target.value } : regra.acaoSenao
                                              };
                                              atualizarRegraApi(acaoIndex, regraIndex, novaRegra);
                                            }}
                                            className="px-2 py-1 border border-gray-300 rounded text-sm bg-white"
                                          >
                                            <option value="">Selecionar campo...</option>
                                            {campos.map((campo: any) => (
                                              <option key={campo.id} value={campo.id}>{campo.nome}</option>
                                            ))}
                                          </select>
                                          <select
                                            value={regra.acaoEntao?.formato || 'numero-empresa'}
                                            onChange={(e) => {
                                              const novaRegra = {
                                                ...regra,
                                                acaoEntao: { ...regra.acaoEntao, formato: e.target.value },
                                                acaoSenao: regra.acaoSenao ? { ...regra.acaoSenao, formato: e.target.value } : regra.acaoSenao
                                              };
                                              atualizarRegraApi(acaoIndex, regraIndex, novaRegra);
                                            }}
                                            className="px-2 py-1 border border-gray-300 rounded text-sm bg-white"
                                          >
                                            <option value="numero-empresa">Número-Empresa (3536-01)</option>
                                            <option value="direto">Valor Direto</option>
                                          </select>
                                        </div>
                                      </div>

                                      {/* ENTÃO (API a chamar) */}
                                      <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                                        <h6 className="text-sm font-medium text-green-800 mb-2">ENTÃO (Chamar API)</h6>
                                        <div className="space-y-2">
                                          <select
                                            value={regra.acaoEntao?.url ? `${regra.acaoEntao.metodo || 'POST'}|${regra.acaoEntao.url}` : ''}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              if (val === '') {
                                                const novaRegra = { ...regra, acaoEntao: { ...regra.acaoEntao, tipo: 'nao_chamar', url: '', metodo: 'POST' } };
                                                atualizarRegraApi(acaoIndex, regraIndex, novaRegra);
                                              } else {
                                                const [metodo, ...urlParts] = val.split('|');
                                                const url = urlParts.join('|');
                                                const novaRegra = { ...regra, acaoEntao: { ...regra.acaoEntao, tipo: 'chamar_api', url, metodo } };
                                                atualizarRegraApi(acaoIndex, regraIndex, novaRegra);
                                              }
                                            }}
                                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white"
                                          >
                                            <option value="">Não chamar API</option>
                                          </select>
                                        </div>
                                      </div>

                                      {/* SENÃO (API alternativa) */}
                                      <div className="bg-orange-50 p-3 rounded-lg border-l-4 border-orange-400">
                                        <h6 className="text-sm font-medium text-orange-800 mb-2">SENÃO (API alternativa)</h6>
                                        <div className="space-y-2">
                                          <select
                                            value={regra.acaoSenao?.url ? `${regra.acaoSenao.metodo || 'POST'}|${regra.acaoSenao.url}` : ''}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              if (val === '') {
                                                const novaRegra = { ...regra, acaoSenao: { ...regra.acaoSenao, tipo: 'nao_chamar', url: '', metodo: 'POST' } };
                                                atualizarRegraApi(acaoIndex, regraIndex, novaRegra);
                                              } else {
                                                const [metodo, ...urlParts] = val.split('|');
                                                const url = urlParts.join('|');
                                                const novaRegra = { ...regra, acaoSenao: { ...regra.acaoSenao, tipo: 'chamar_api', url, metodo } };
                                                atualizarRegraApi(acaoIndex, regraIndex, novaRegra);
                                              }
                                            }}
                                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white"
                                          >
                                            <option value="">Não chamar API</option>
                                          </select>
                                        </div>
                                      </div>
                                    </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Configuração específica para Criar Card Conectado */}
                          {acao.tipo === 'criar_card_conectado' && (
                            <div className="space-y-3">
                              {/* Seletor de configuração principal */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Configuração de Card Conectado</label>
                                <select
                                  value={acao.configuracao.configuracaoCardConectadoId || ''}
                                  onChange={(e) => atualizarAcao(acaoIndex, {
                                    ...acao,
                                    configuracao: { ...acao.configuracao, configuracaoCardConectadoId: e.target.value }
                                  })}
                                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                >
                                  <option value="">Selecione uma configuração salva</option>
                                  {configsCardConectado.map((cfg: any) => (
                                    <option key={cfg.id || cfg._id} value={cfg.id || cfg._id}>
                                      {cfg.nome} — {flows.find(f => f.id === cfg.fluxoDestinoId)?.nome || cfg.fluxoDestinoId}
                                    </option>
                                  ))}
                                </select>
                                {configsCardConectado.length === 0 && (
                                  <p className="text-[0.6rem] text-amber-600 mt-1">
                                    Nenhuma configuração de card conectado encontrada. Configure em Cards Conectados primeiro.
                                  </p>
                                )}
                              </div>

                              {/* Preview da configuração selecionada */}
                              {acao.configuracao.configuracaoCardConectadoId && (() => {
                                const cfgSel = configsCardConectado.find((c: any) => (c.id || c._id) === acao.configuracao.configuracaoCardConectadoId);
                                if (!cfgSel) return null;
                                const fluxoDestino = flows.find(f => f.id === cfgSel.fluxoDestinoId);
                                const etapaDestino = fluxoDestino?.etapas?.find((e: any) => e.id === cfgSel.etapaDestinoId);
                                return (
                                  <div className="bg-violet-50 p-3 rounded-lg border border-violet-200 text-xs">
                                    <p className="font-medium text-violet-800 mb-1">{cfgSel.nome}</p>
                                    <div className="text-violet-700 space-y-0.5">
                                      <p>Fluxo destino: <strong>{fluxoDestino?.nome || cfgSel.fluxoDestinoId}</strong></p>
                                      <p>Etapa destino: <strong>{etapaDestino?.nome || cfgSel.etapaDestinoId}</strong></p>
                                      {cfgSel.prefixoTitulo && <p>Prefixo: <strong>{cfgSel.prefixoTitulo}</strong></p>}
                                      <p>Campos mapeados: <strong>{cfgSel.mapeamentoCampos?.length || 0}</strong></p>
                                      <p className="flex items-center gap-2">
                                        {cfgSel.copiarHistorico && <span className="bg-violet-200 px-1 rounded">Copia histórico</span>}
                                        {cfgSel.copiarAnexos && <span className="bg-violet-200 px-1 rounded">Copia anexos</span>}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Regras condicionais */}
                              {(!acao.configuracao.regrasCardConectado || acao.configuracao.regrasCardConectado.length === 0) ? (
                                <div className="bg-violet-50 p-3 rounded-lg text-center">
                                  <h4 className="text-sm font-medium text-violet-800 mb-2">Regras Condicionais (Opcional)</h4>
                                  <p className="text-xs text-violet-700 mb-3">
                                    Opcional: crie o card conectado apenas quando uma condição for atendida (SE/ENTÃO/SENÃO)
                                  </p>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      adicionarRegraCardConectado(acaoIndex);
                                    }}
                                    className="bg-violet-600 text-white px-4 py-2 rounded text-sm hover:bg-violet-700 inline-flex items-center gap-2"
                                  >
                                    <PlusIcon className="h-4 w-4" />
                                    Adicionar Regra Condicional
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-medium text-gray-800">
                                      Regras Condicionais ({acao.configuracao.regrasCardConectado.length})
                                    </h4>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        adicionarRegraCardConectado(acaoIndex);
                                      }}
                                      className="bg-violet-600 text-white px-3 py-1.5 rounded text-xs hover:bg-violet-700 flex items-center gap-1"
                                    >
                                      <PlusIcon className="h-3 w-3" /> Nova Regra
                                    </button>
                                  </div>

                                  {acao.configuracao.regrasCardConectado.map((regra: any, regraIndex: number) => {
                                    const etapaParaCampo = regra.condicao?.etapaOrigemCampoId || automacao.etapaOrigemId;
                                    const etapaCampoSelecionada = selectedFlow?.etapas?.find(e => e.id === etapaParaCampo);
                                    const camposDisponiveis = etapaCampoSelecionada?.campos || [];

                                    return (
                                      <div key={regraIndex} className="border-2 border-violet-200 rounded-lg p-4 space-y-3 bg-violet-50/50">
                                        <div className="flex items-center justify-between">
                                          <h5 className="text-sm font-semibold text-violet-900">Regra {regraIndex + 1}</h5>
                                          <button
                                            onClick={() => removerRegraCardConectado(acaoIndex, regraIndex)}
                                            className="text-red-600 hover:text-red-800 p-1"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </button>
                                        </div>

                                        {/* Condição SE */}
                                        <div className="bg-white p-4 rounded-lg border-l-4 border-violet-500">
                                          <h6 className="text-sm font-medium text-violet-800 mb-3">SE (Condição)</h6>
                                          
                                          {/* Seletor de Etapa */}
                                          <div className="mb-3">
                                            <label className="block text-xs text-gray-600 mb-1">🔀 Buscar campo da etapa:</label>
                                            <select
                                              value={regra.condicao?.etapaOrigemCampoId || automacao.etapaOrigemId}
                                              onChange={(e) => {
                                                atualizarRegraCardConectado(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  condicao: { ...regra.condicao, etapaOrigemCampoId: e.target.value, campoId: '' }
                                                });
                                              }}
                                              className="w-full px-3 py-2 border border-violet-300 rounded-lg bg-violet-50 text-sm"
                                            >
                                              <option value={automacao.etapaOrigemId}>
                                                ✓ {selectedFlow?.etapas?.find(e => e.id === automacao.etapaOrigemId)?.nome || 'Etapa atual'}
                                              </option>
                                              {selectedFlow?.etapas?.filter(e => e.id !== automacao.etapaOrigemId).map(etapa => (
                                                <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>
                                              ))}
                                            </select>
                                          </div>

                                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <div>
                                              <label className="block text-xs text-gray-600 mb-1">Campo</label>
                                              <select
                                                value={regra.condicao?.campoId || ''}
                                                onChange={(e) => atualizarRegraCardConectado(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  condicao: { ...regra.condicao, campoId: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                              >
                                                <option value="">Selecione um campo</option>
                                                {camposDisponiveis.map((c: any) => (
                                                  <option key={c.id} value={c.id}>{c.nome}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <div>
                                              <label className="block text-xs text-gray-600 mb-1">Operador</label>
                                              <select
                                                value={regra.condicao?.operador || 'preenchido'}
                                                onChange={(e) => atualizarRegraCardConectado(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  condicao: { ...regra.condicao, operador: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                              >
                                                <option value="preenchido">está preenchido</option>
                                                <option value="vazio">está vazio</option>
                                                <option value="igual">é igual a</option>
                                                <option value="diferente">é diferente de</option>
                                                <option value="contem">contém</option>
                                                <option value="nao_contem">não contém</option>
                                                <option value="maior">é maior que</option>
                                                <option value="menor">é menor que</option>
                                                <option value="maior_igual">é igual ou maior que</option>
                                                <option value="dias_restantes">faltam X dias ou menos</option>
                                              </select>
                                            </div>
                                            <div>
                                              <label className="block text-xs text-gray-600 mb-1">Valor</label>
                                              <input
                                                type="text"
                                                value={regra.condicao?.valor || ''}
                                                onChange={(e) => atualizarRegraCardConectado(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  condicao: { ...regra.condicao, valor: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="Valor para comparação"
                                                disabled={['preenchido', 'vazio'].includes(regra.condicao?.operador || '')}
                                              />
                                            </div>
                                          </div>
                                        </div>

                                        {/* ENTÃO */}
                                        <div className="bg-white p-4 rounded-lg border-l-4 border-indigo-500">
                                          <h6 className="text-sm font-medium text-indigo-800 mb-3">ENTÃO</h6>
                                          <div className="grid grid-cols-2 gap-3">
                                            <div>
                                              <label className="block text-xs text-gray-600 mb-1">Ação</label>
                                              <select
                                                value={regra.acaoEntao?.tipo || 'criar'}
                                                onChange={(e) => atualizarRegraCardConectado(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  acaoEntao: { ...regra.acaoEntao, tipo: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                              >
                                                <option value="criar">Criar card conectado</option>
                                                <option value="nao_criar">Não criar</option>
                                              </select>
                                            </div>
                                            {regra.acaoEntao?.tipo === 'criar' && (
                                              <div>
                                                <label className="block text-xs text-gray-600 mb-1">Configuração</label>
                                                <select
                                                  value={regra.acaoEntao?.configuracaoCardConectadoId || acao.configuracao.configuracaoCardConectadoId || ''}
                                                  onChange={(e) => atualizarRegraCardConectado(acaoIndex, regraIndex, {
                                                    ...regra,
                                                    acaoEntao: { ...regra.acaoEntao, configuracaoCardConectadoId: e.target.value }
                                                  })}
                                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                >
                                                  <option value="">Usar configuração principal</option>
                                                  {configsCardConectado.map((cfg: any) => (
                                                    <option key={cfg.id || cfg._id} value={cfg.id || cfg._id}>
                                                      {cfg.nome}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            )}
                                          </div>
                                        </div>

                                        {/* SENÃO */}
                                        <div className="bg-white p-4 rounded-lg border-l-4 border-orange-400">
                                          <h6 className="text-sm font-medium text-orange-700 mb-3">SENÃO</h6>
                                          <div className="grid grid-cols-2 gap-3">
                                            <div>
                                              <label className="block text-xs text-gray-600 mb-1">Ação</label>
                                              <select
                                                value={regra.acaoSenao?.tipo || 'nao_criar'}
                                                onChange={(e) => atualizarRegraCardConectado(acaoIndex, regraIndex, {
                                                  ...regra,
                                                  acaoSenao: { ...regra.acaoSenao, tipo: e.target.value }
                                                })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                              >
                                                <option value="nao_criar">Não criar</option>
                                                <option value="criar">Criar card conectado</option>
                                              </select>
                                            </div>
                                            {regra.acaoSenao?.tipo === 'criar' && (
                                              <div>
                                                <label className="block text-xs text-gray-600 mb-1">Configuração</label>
                                                <select
                                                  value={regra.acaoSenao?.configuracaoCardConectadoId || acao.configuracao.configuracaoCardConectadoId || ''}
                                                  onChange={(e) => atualizarRegraCardConectado(acaoIndex, regraIndex, {
                                                    ...regra,
                                                    acaoSenao: { ...regra.acaoSenao, configuracaoCardConectadoId: e.target.value }
                                                  })}
                                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                >
                                                  <option value="">Usar configuração principal</option>
                                                  {configsCardConectado.map((cfg: any) => (
                                                    <option key={cfg.id || cfg._id} value={cfg.id || cfg._id}>
                                                      {cfg.nome}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Configuração para Mover Card Conectado */}
                          {acao.tipo === 'mover_card_conectado' && (() => {
                            const cfg = acao.configuracao.moverCardConectado || {};
                            const fluxoConectado = flows.find(f => f.id === cfg.fluxoDestinoConectadoId);
                            const fluxoAtual = flows.find(f => f.id === cfg.fluxoDestinoAtualId);
                            
                            const updateCfg = (updates: any) => {
                              atualizarAcao(acaoIndex, {
                                ...acao,
                                configuracao: {
                                  ...acao.configuracao,
                                  moverCardConectado: { ...cfg, ...updates }
                                }
                              });
                            };

                            return (
                              <div className="space-y-4">
                                {/* Explicação */}
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                  <p className="text-xs text-amber-800">
                                    <strong>Como funciona:</strong> Quando este card chegar nesta etapa, o sistema vai buscar o card conectado 
                                    (que veio de uma conexão anterior) e movê-lo para a etapa que você configurar abaixo. 
                                    Opcionalmente, também pode mover o card atual para outra etapa/fluxo.
                                  </p>
                                </div>

                                {/* Direção da busca */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Buscar card conectado como
                                  </label>
                                  <select
                                    value={cfg.direcao || 'origem'}
                                    onChange={(e) => updateCfg({ direcao: e.target.value })}
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                  >
                                    <option value="origem">Origem (o card atual é o destino da conexão, buscar o card de origem)</option>
                                    <option value="destino">Destino (o card atual é a origem da conexão, buscar o card de destino)</option>
                                  </select>
                                  <p className="text-[0.6rem] text-gray-500 mt-0.5">
                                    Ex: Se o card atual veio do fluxo "Compras" e quer mover o card original em "Solicitações TI", use "Origem".
                                  </p>
                                </div>

                                {/* Destino do Card Conectado */}
                                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-3">
                                  <h5 className="text-xs font-semibold text-indigo-800 flex items-center gap-1.5">
                                    <ArrowsRightLeftIcon className="h-4 w-4" />
                                    Destino do Card Conectado (que será movido)
                                  </h5>
                                  
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Fluxo destino</label>
                                      <select
                                        value={cfg.fluxoDestinoConectadoId || ''}
                                        onChange={(e) => updateCfg({ fluxoDestinoConectadoId: e.target.value, etapaDestinoConectadoId: '' })}
                                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                      >
                                        <option value="">Selecione o fluxo</option>
                                        {flows.map(flow => (
                                          <option key={flow.id} value={flow.id}>{flow.nome}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Etapa destino</label>
                                      <select
                                        value={cfg.etapaDestinoConectadoId || ''}
                                        onChange={(e) => updateCfg({ etapaDestinoConectadoId: e.target.value })}
                                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        disabled={!fluxoConectado}
                                      >
                                        <option value="">Selecione a etapa</option>
                                        {fluxoConectado?.etapas?.map((etapa: any) => (
                                          <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                </div>

                                {/* Destino do Card Atual (opcional) */}
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-3">
                                  <h5 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                                    <ArrowRightIcon className="h-4 w-4" />
                                    Destino do Card Atual (opcional)
                                  </h5>
                                  <p className="text-[0.6rem] text-gray-500">
                                    Se desejar, mova também o card atual para outro fluxo/etapa após a conexão.
                                  </p>
                                  
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Fluxo destino (atual)</label>
                                      <select
                                        value={cfg.fluxoDestinoAtualId || ''}
                                        onChange={(e) => updateCfg({ fluxoDestinoAtualId: e.target.value, etapaDestinoAtualId: '' })}
                                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                      >
                                        <option value="">Não mover card atual</option>
                                        {flows.map(flow => (
                                          <option key={flow.id} value={flow.id}>{flow.nome}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">Etapa destino (atual)</label>
                                      <select
                                        value={cfg.etapaDestinoAtualId || ''}
                                        onChange={(e) => updateCfg({ etapaDestinoAtualId: e.target.value })}
                                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        disabled={!fluxoAtual}
                                      >
                                        <option value="">Selecione a etapa</option>
                                        {fluxoAtual?.etapas?.map((etapa: any) => (
                                          <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                </div>

                                {/* Opções adicionais */}
                                <div className="flex items-center gap-3">
                                  <label className="flex items-center gap-2 text-xs text-gray-700">
                                    <input
                                      type="checkbox"
                                      checked={cfg.copiarDadosConexao !== false}
                                      onChange={(e) => updateCfg({ copiarDadosConexao: e.target.checked })}
                                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    Copiar dados da conexão para o card movido
                                  </label>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Configuração específica para Criar Card de Modelo (criar_card_template) */}
                          {acao.tipo === 'criar_card_template' && (() => {
                            const cfg = (acao.configuracao as any).criarCardTemplate || { fluxoDestinoId: '', etapaDestinoId: '', templateId: '', responsavelId: '', tituloCustom: '' };
                            const updateCfg = (patch: any) => atualizarAcao(acaoIndex, {
                              ...acao,
                              configuracao: {
                                ...acao.configuracao,
                                criarCardTemplate: { ...cfg, ...patch },
                              },
                            });
                            const fluxoDestino: any = flows.find((f: any) => f.id === cfg.fluxoDestinoId);
                            const etapaDestino: any = fluxoDestino?.etapas?.find((e: any) => e.id === cfg.etapaDestinoId);
                            const templates: any[] = Array.isArray(etapaDestino?.cardsPersonalizados) ? etapaDestino.cardsPersonalizados : [];
                            return (
                              <div className="p-3 space-y-3">
                                <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-[11px] text-violet-800 leading-snug">
                                  Essa ação cria um card novo a partir de um <strong>modelo</strong> salvo numa etapa.
                                  Combinada com o gatilho <strong>"Agendado"</strong>, você consegue criar cards recorrentes
                                  (ex: todo dia 5 de cada mês) já atribuídos a um responsável.
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Fluxo de destino</label>
                                    <select
                                      value={cfg.fluxoDestinoId || ''}
                                      onChange={(e) => updateCfg({ fluxoDestinoId: e.target.value, etapaDestinoId: '', templateId: '' })}
                                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                    >
                                      <option value="">Selecione o fluxo</option>
                                      {flows.map((flow: any, idx: number) => (
                                        <option key={flow.id || `flow-${idx}`} value={flow.id}>{flow.nome}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Etapa de destino</label>
                                    <select
                                      value={cfg.etapaDestinoId || ''}
                                      onChange={(e) => updateCfg({ etapaDestinoId: e.target.value, templateId: '' })}
                                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent disabled:bg-gray-100"
                                      disabled={!fluxoDestino}
                                    >
                                      <option value="">Selecione a etapa</option>
                                      {fluxoDestino?.etapas?.map((etapa: any, idx: number) => (
                                        <option key={etapa.id || `etapa-${idx}`} value={etapa.id}>
                                          {etapa.nome}
                                          {Array.isArray(etapa.cardsPersonalizados) && etapa.cardsPersonalizados.length > 0
                                            ? ` (${etapa.cardsPersonalizados.length} modelo${etapa.cardsPersonalizados.length > 1 ? 's' : ''})`
                                            : ''}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Modelo (Card Personalizado)</label>
                                  {etapaDestino && templates.length === 0 ? (
                                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                                      ⚠️ Esta etapa ainda não tem modelos. Vá em <strong>Configurar Etapa → Cards Personalizados</strong> e crie um modelo primeiro.
                                    </div>
                                  ) : (
                                    <select
                                      value={cfg.templateId || ''}
                                      onChange={(e) => updateCfg({ templateId: e.target.value })}
                                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent disabled:bg-gray-100"
                                      disabled={!etapaDestino || templates.length === 0}
                                    >
                                      <option value="">Selecione um modelo</option>
                                      {templates.map((t: any, idx: number) => (
                                        <option key={t.id || `tpl-${idx}`} value={t.id}>
                                          {t.nome}
                                          {t.titulo ? ` — "${t.titulo}"` : ''}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Responsável (opcional)</label>
                                    <select
                                      value={cfg.responsavelId || ''}
                                      onChange={(e) => updateCfg({ responsavelId: e.target.value })}
                                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                    >
                                      <option value="">— Sem responsável fixo —</option>
                                      {colaboradores.map((c: any, idx: number) => (
                                        <option key={c.id || c._id || `colab-${idx}`} value={c.id || c._id}>{c.nome}</option>
                                      ))}
                                    </select>
                                    <p className="text-[10px] text-gray-500 mt-0.5">Se vazio, usa o padrão da etapa de destino.</p>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Título customizado (opcional)</label>
                                    <input
                                      type="text"
                                      value={cfg.tituloCustom || ''}
                                      onChange={(e) => updateCfg({ tituloCustom: e.target.value })}
                                      placeholder="Ex: Fechamento DP {{mes}}/{{ano}}"
                                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                    />
                                    <p className="text-[10px] text-gray-500 mt-0.5">Use {'{{mes}}'}, {'{{ano}}'}, {'{{dia}}'} para substituir pela data atual.</p>
                                  </div>
                                </div>

                                {/* Campos adicionais (pré-preencher dados dinâmicos) */}
                                {etapaDestino && Array.isArray((etapaDestino as any).campos) && (etapaDestino as any).campos.length > 0 && (() => {
                                  const camposEtapa: any[] = (etapaDestino as any).campos || [];
                                  const camposExtras: any[] = Array.isArray(cfg.camposExtras) ? cfg.camposExtras : [];
                                  const addCampo = () => {
                                    const usados = new Set(camposExtras.map((c: any) => c.campoId));
                                    const disponivel = camposEtapa.find((c: any) => !usados.has(c.id));
                                    updateCfg({ camposExtras: [...camposExtras, { campoId: disponivel?.id || '', valor: '' }] });
                                  };
                                  const updateCampo = (i: number, patch: any) => {
                                    const arr = camposExtras.map((c: any, idx: number) => idx === i ? { ...c, ...patch } : c);
                                    updateCfg({ camposExtras: arr });
                                  };
                                  const removeCampo = (i: number) => {
                                    updateCfg({ camposExtras: camposExtras.filter((_: any, idx: number) => idx !== i) });
                                  };
                                  return (
                                    <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                                      <div className="flex items-center justify-between">
                                        <h5 className="text-xs font-semibold text-gray-700">Campos adicionais (pré-preencher)</h5>
                                        <button
                                          type="button"
                                          onClick={addCampo}
                                          className="text-[11px] px-2 py-1 bg-violet-600 text-white rounded hover:bg-violet-700"
                                        >
                                          + Adicionar campo
                                        </button>
                                      </div>
                                      <p className="text-[10px] text-gray-500 leading-snug">
                                        Sobrescreve os valores do modelo. Suporta placeholders: <code className="bg-white px-1 rounded">{'{{hoje}}'}</code>, <code className="bg-white px-1 rounded">{'{{hoje+30d}}'}</code>, <code className="bg-white px-1 rounded">{'{{hoje+1m}}'}</code>, <code className="bg-white px-1 rounded">{'{{hoje+2h}}'}</code>, <code className="bg-white px-1 rounded">{'{{dia_mes:5}}'}</code> (próximo dia 5 do mês), <code className="bg-white px-1 rounded">{'{{ultimo_dia_mes}}'}</code>, <code className="bg-white px-1 rounded">{'{{dia}}'}</code>, <code className="bg-white px-1 rounded">{'{{mes}}'}</code>, <code className="bg-white px-1 rounded">{'{{ano}}'}</code>.
                                      </p>
                                      {camposExtras.length === 0 ? (
                                        <p className="text-[11px] text-gray-400 italic">Nenhum campo adicional configurado.</p>
                                      ) : (
                                        <div className="space-y-2">
                                          {camposExtras.map((ce: any, i: number) => {
                                            const campoDef = camposEtapa.find((c: any) => c.id === ce.campoId);
                                            const tipoCampo = campoDef?.tipo || '';
                                            const isData = tipoCampo.includes('data') || tipoCampo === 'date';
                                            const isLista = tipoCampo === 'lista';
                                            const isCheckbox = tipoCampo === 'checkbox' || tipoCampo === 'checkbox_single' || tipoCampo === 'checkbox_multi';
                                            const opcoesCampo: string[] = Array.isArray(campoDef?.opcoes) ? campoDef.opcoes : [];
                                            const coresCampo: Record<string, string> = (campoDef as any)?.coresOpcoes || {};
                                            const valoresSelecionados: string[] = Array.isArray(ce.valor) ? ce.valor.map((v: any) => String(v)) : (ce.valor ? [String(ce.valor)] : []);
                                            return (
                                              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start">
                                                <select
                                                  value={ce.campoId || ''}
                                                  onChange={(e) => {
                                                    const novoCampoDef = camposEtapa.find((c: any) => c.id === e.target.value);
                                                    const novoTipo = novoCampoDef?.tipo || '';
                                                    const novoIsCheckbox = novoTipo === 'checkbox' || novoTipo === 'checkbox_single' || novoTipo === 'checkbox_multi';
                                                    updateCampo(i, { campoId: e.target.value, valor: novoIsCheckbox ? [] : '' });
                                                  }}
                                                  className="px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-violet-500"
                                                >
                                                  <option value="">Selecione o campo</option>
                                                  {camposEtapa.map((c: any) => (
                                                    <option key={c.id} value={c.id}>
                                                      {c.nome} ({c.tipo})
                                                    </option>
                                                  ))}
                                                </select>
                                                {isLista ? (
                                                  <select
                                                    value={typeof ce.valor === 'string' ? ce.valor : ''}
                                                    onChange={(e) => updateCampo(i, { valor: e.target.value })}
                                                    className="px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-violet-500"
                                                  >
                                                    <option value="">Selecione uma opção</option>
                                                    {opcoesCampo.map((op: string) => (
                                                      <option key={op} value={op}>{op}</option>
                                                    ))}
                                                  </select>
                                                ) : isCheckbox ? (
                                                  <div className="border border-gray-300 rounded p-1.5 max-h-32 overflow-y-auto bg-white">
                                                    {opcoesCampo.length === 0 ? (
                                                      <p className="text-[10px] text-gray-400 italic px-1">Este campo não possui opções configuradas</p>
                                                    ) : opcoesCampo.map((op: string) => {
                                                      const checked = valoresSelecionados.includes(op);
                                                      const cor = coresCampo[op];
                                                      return (
                                                        <label key={op} className="flex items-center gap-1.5 text-[11px] px-1 py-0.5 hover:bg-gray-50 rounded cursor-pointer">
                                                          <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={(e) => {
                                                              const novoArr = e.target.checked
                                                                ? [...valoresSelecionados, op]
                                                                : valoresSelecionados.filter(v => v !== op);
                                                              updateCampo(i, { valor: novoArr });
                                                            }}
                                                            className="w-3 h-3"
                                                          />
                                                          {cor ? (
                                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: cor, color: '#fff' }}>{op}</span>
                                                          ) : (
                                                            <span>{op}</span>
                                                          )}
                                                        </label>
                                                      );
                                                    })}
                                                  </div>
                                                ) : isData ? (
                                                  (() => {
                                                    const valStr = typeof ce.valor === 'string' ? ce.valor : '';
                                                    const isPlaceholder = valStr.includes('{{');
                                                    const aplicarPreset = (preset: string) => {
                                                      if (!preset) return;
                                                      let novoValor = '';
                                                      if (preset === 'hoje') novoValor = '{{hoje}}';
                                                      else if (preset === 'hoje+Nd') {
                                                        const n = window.prompt('Daqui a quantos dias?', '30');
                                                        if (!n) return; novoValor = `{{hoje+${parseInt(n) || 1}d}}`;
                                                      } else if (preset === 'hoje+Nm') {
                                                        const n = window.prompt('Daqui a quantos meses?', '1');
                                                        if (!n) return; novoValor = `{{hoje+${parseInt(n) || 1}m}}`;
                                                      } else if (preset === 'hoje+Nh') {
                                                        const n = window.prompt('Daqui a quantas horas?', '1');
                                                        if (!n) return; novoValor = `{{hoje+${parseInt(n) || 1}h}}`;
                                                      } else if (preset === 'hoje+Nw') {
                                                        const n = window.prompt('Daqui a quantas semanas?', '1');
                                                        if (!n) return; novoValor = `{{hoje+${parseInt(n) || 1}w}}`;
                                                      } else if (preset === 'dia_mes') {
                                                        const n = window.prompt('Dia do mês (1-31). Se o dia já passou neste mês, usa o próximo:', '5');
                                                        if (!n) return;
                                                        const d = Math.min(31, Math.max(1, parseInt(n) || 1));
                                                        novoValor = `{{dia_mes:${d}}}`;
                                                      } else if (preset === 'ultimo_dia_mes') {
                                                        novoValor = '{{ultimo_dia_mes}}';
                                                      } else if (preset === 'calendario') {
                                                        novoValor = '';
                                                      }
                                                      updateCampo(i, { valor: novoValor });
                                                    };
                                                    return (
                                                      <div className="flex items-center gap-1">
                                                        <input
                                                          type={isPlaceholder ? 'text' : 'date'}
                                                          value={valStr}
                                                          onChange={(e) => updateCampo(i, { valor: e.target.value })}
                                                          placeholder={isPlaceholder ? '{{hoje+30d}}' : ''}
                                                          className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-violet-500"
                                                        />
                                                        <select
                                                          value=""
                                                          onChange={(e) => { aplicarPreset(e.target.value); e.target.value = ''; }}
                                                          title="Inserir placeholder dinâmico"
                                                          className="px-1 py-1 text-[10px] border border-gray-300 rounded hover:bg-gray-100 bg-white"
                                                        >
                                                          <option value="">{'{{…}}'}</option>
                                                          <option value="calendario">📅 Calendário (data fixa)</option>
                                                          <option value="hoje">Hoje</option>
                                                          <option value="hoje+Nd">Hoje + N dias…</option>
                                                          <option value="hoje+Nw">Hoje + N semanas…</option>
                                                          <option value="hoje+Nm">Hoje + N meses…</option>
                                                          <option value="hoje+Nh">Hoje + N horas…</option>
                                                          <option value="dia_mes">Dia X de cada mês…</option>
                                                          <option value="ultimo_dia_mes">Último dia do mês</option>
                                                        </select>
                                                      </div>
                                                    );
                                                  })()
                                                ) : (
                                                  <input
                                                    type="text"
                                                    value={typeof ce.valor === 'string' ? ce.valor : ''}
                                                    onChange={(e) => updateCampo(i, { valor: e.target.value })}
                                                    placeholder={'Valor (ou use {{placeholders}})'}
                                                    className="px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-violet-500"
                                                  />
                                                )}
                                                <button
                                                  type="button"
                                                  onClick={() => removeCampo(i)}
                                                  className="px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded"
                                                  title="Remover"
                                                >
                                                  🗑️
                                                </button>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                          })()}

                        </div>
                        )}
                      </div>
                      );
                    })}
                  </div>

               
                  {previewMode && automacao.acoes.length > 0 && (
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                      <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <EyeIcon className="h-4 w-4" />
                        Resumo da Automação
                      </h3>
                      <div className="space-y-2 text-sm text-gray-700">
                        <p><span className="font-medium">Nome:</span> {automacao.nome || 'Sem nome'}</p>
                        <p><span className="font-medium">Fluxo:</span> {selectedFlow?.nome}</p>
                        <p><span className="font-medium">Etapa de origem:</span> {etapaOrigem?.nome}</p>
                        <p><span className="font-medium">Ações:</span> {automacao.acoes.length} configurada(s)</p>
                        <div className="mt-2 p-2 bg-white rounded border-l-4 border-blue-400">
                          <p className="text-[0.65rem] text-gray-600 uppercase tracking-wider font-medium mb-1">Fluxo de execução</p>
                          <p className="text-xs">
                            Quando um card estiver na etapa "<strong>{etapaOrigem?.nome}</strong>", 
                            o sistema executará {automacao.acoes.length === 1 ? 'a ação' : 'as ações'} configurada(s) 
                            de acordo com as regras definidas em cada ação.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                </>
              )}
            </>
          )}

        </div>

        {/* Footer Compacto Sticky */}
        <div className="px-4 py-2 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            {automacaoExistente ? 'Atualizar' : 'Salvar'} Automação
          </button>
        </div>
      </div>
    </div>
  );
};

export default NovaAutomacao;
